
import { MarkerType } from "@/scripts/mapping/markerTypes";
import axios, { CancelTokenSource } from "axios";
import { Component, Watch, Prop, Mixins } from "vue-property-decorator";
import { Getter, Mutation, namespace } from "vuex-class";
import { axiosInstance } from "@/axios.instance";
import { FeaturesList, UserPermissions } from "@/store/types";
import {
	IEnhancedMapping,
	IMap,
	IMarker,
	ICircle,
	ILatLng,
	ILatLngBounds,
	MarkerIcons,
	SVMarker,
	MapMarkerManager,
	MapInfoWindowService,
	IMapInfoWindowService,
	MapMarkerClustererService,
	MapMarkerClustererOptions,
	ClusterContextMenuResponse,
	MarkerMetadata,
	DisplayItem,
	MapLayerItemType as MapLayerItemTypeEnum,
	getEnhancedMapping,
	convertStringToLatLng,
	stringToBounds,
	union,
	extend
} from "@sureview/v2-mapping-saas";
import MapMarkerClustererMixin from "@/mixins/MapMarkerClustererMixin";
import { get, keyBy, differenceBy, cloneDeep } from "lodash";
import { darkMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";
import { ActiveMapItem, EventDetails, IconType } from "@/store/site-monitor/types";

import GPSService from "@/services/gps.service";
import { AssetMapLayerItem } from './mobile-assets/types';
import { FilteredEvent, AlarmQueueFilterID } from '@/store/eventqueue/types';
import {
	LocationBounds,
	LayerMap,
	ItemMap,
	ItemTypeObjectMap,
	LayerTypeMap,
	ItemObjectMap,
	MarkerArrayMap,
	MapCameraDevice,
	SubscriberExpiry,
	EnhancedMapMode,
} from "@/types/EnhancedSingleMapTypes";
import Multiselect from "vue-multiselect";
import { MapBounds, MapData, MapLayerItemType, MapLayer, MapLayerItemTypeIds, MapLayerItem, AreaMapDetails, AdvancedFilters, AdvancedFilterOption, DefaultViewTriggers } from "@/store/map-layers/types";
import RegionalHandlerMixin from '@/mixins/RegionalHandlerMixin';
import router from '@/router';
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import { AreaNode } from '@/types/sv-data/groups/AreaNode';
import CameraModal from './media/CameraModal.vue';
import FieldOpsMixin from "@/mixins/FieldOpsMixin";
import EventTypes from '@/types/sv-data/enums/EventTypes';
import  {NotificationOptions } from "vue-notification"
import api from '@/services/api.service';
import MapLabel from '@/scripts/mapping/mapLabel';
import { mapRegionMixins, stringMixin, formatDateMixin } from '@/mixins'
import { SituationalAwarenessEventQueue } from '@/store/situational-awareness/types';
import { ExternalMapLayer } from "@/store/external-map-layers/types";
import AreaDetails from "@/components/AreaDetails.vue";
import EnhancedSingleMapControls from "./mapping/EnhancedSingleMapControls.vue";
import AreaDateTimeDisplay from "./AreaDateTimeDisplay.vue";
import MapMarkerMixin from "@/mixins/map-feature-mixins/MapMarkerMixin";
import MapInfoWindowMixin from '@/mixins/MapInfoWindowMixin';
import AppliancePollingMixin from "@/mixins/AppliancePollingMixin";
import { MapState } from '@/store/map/types';
import { Md5 } from "ts-md5";

const MapLayers = namespace("mapLayers");
const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const Eventqueue = namespace("eventqueue");
const ManualRaiseStore = namespace("manualRaise");
const FieldOpsStore = namespace("fieldOps");
const ExternalMapLayerStore = namespace("externalMapLayers");
const SchedulesStore = namespace("schedules");
const MapStore = namespace("map");
const AreaNotes = namespace("areaNotes");

const geoCoder = new GPSService();
const { ellipseAfterX } = stringMixin.methods;
const formatDateMethod = formatDateMixin.methods;
const fromNow = formatDateMethod.fromNow.bind(formatDateMethod);

const {
	getRegionBounds,
	getPolygonOptionsForRegion,
} = mapRegionMixins.methods;

@Component({
	components: {
		"multiselect": Multiselect,
		"area-tree-select": AreaTreeSelect,
		"camera-modal" : CameraModal,
		"area-details": AreaDetails,
		"enhanced-single-map-controls": EnhancedSingleMapControls,
		"area-date-time-display": AreaDateTimeDisplay
	}
})
export default class EnhancedSingleMap extends Mixins(Mixins(RegionalHandlerMixin, FieldOpsMixin, MapMarkerClustererMixin, MapInfoWindowMixin, MapMarkerMixin), AppliancePollingMixin) {
	// ----------------------------------------------------- Global Logic  ---------------------------------------------------------------------------------
	@Prop({ default: () => 19 })
	public zoomLevel: number;

	@Prop({ default: () => false })
	private showMapToggle: boolean; //Feature flag or just remove

	@Prop()
	private mapStyle!: any;

	@Prop({type:Number, default: EnhancedMapMode.SiteMonitor})
	private enhancedMapMode: EnhancedMapMode;

	@Prop({type: Boolean, default: false})
	private addingAssetLocation: boolean;

	@Prop({type: Boolean, default: false})
	private useAdvancedFilters: boolean;

	@Prop({type: Boolean, default: false})
	private isEventEditMode: boolean;

	public $refs: any = {
		mappingContainer: HTMLElement
	};

	public elevation: number = 0;
	public displayElevation: string = "";
	private defaultMinimumZoomLevel: number = 11;

	public layersAdded: LayerMap = new LayerMap();
	public itemsAdded: ItemMap = new ItemMap();
	public markersByTypeAndObject: ItemTypeObjectMap = new ItemTypeObjectMap();
	public mapCenter: ILatLng | null = null;
	public enhancedMapping: IEnhancedMapping | null = null;
	public map: IMap | null = null;
	public layersVisible = false;
	public elevationList: any[] = [];
	private noMapData: boolean = false;
	private mapType: string = "";
	private eventQueueTimer = null;
	private centerPositionTimeout: NodeJS.Timeout | null= null;
	private displayAssetItemsTimeout: NodeJS.Timeout | null= null;
	private searchAutocompleteTimeout: NodeJS.Timeout | null= null;
	private externalMapLayersTimeout: NodeJS.Timeout | null= null;
	private openAlarmOverlay: boolean = false;
	private openFieldOpsOverlay: boolean = false;
	private overlayAsset: any = null;
	private alarmToOpen: any = null;
	private viewingCameras: MapCameraDevice[] = [];
	private openSearch: boolean = false;
	private alarmStyle: HTMLStyleElement;
	private areaMarkers: SVMarker[] = [];
	private selectedArea: DisplayItem = null;
	private loadMapDataToken: CancelTokenSource | null = null;
	private mapMarkerClustererService: MapMarkerClustererService | null;
	private mapMarkerClustererOptions: MapMarkerClustererOptions | null;
	private mapMarkerManager: MapMarkerManager | null;
	private defaultMapMarkerKey: string | null;
	private hiddenMapLayerItemTypes: Set<number> = new Set<number>();
	private mapInfoWindowService: IMapInfoWindowService | null = null;
	private clusterMarkerClicked: boolean = false;
	private canShareEventWithAsset: boolean = false;

	// Tracking for proper cleanup
	private mapEventListeners: google.maps.MapsEventListener[] = [];
	private isDestroyed: boolean = false; // Flag to stop async operations during destruction

	// uninitialized intentionally to make it non-reactive
	private previousEvents: any[];

	@MapLayers.State private mapData: MapData;
	@MapLayers.State("advancedFilters") private advancedFilters: AdvancedFilters;
	@MapLayers.Action private loadMapDataByBounds: ({ mapBounds, cancelToken }) => Promise<void>;
	@MapLayers.Action private loadMapLayersItemTypes: () => Promise<void>;
	@MapLayers.Getter("getMapLayerItemTypes") private mapItemTypes: MapLayerItemType[];
	@MapLayers.State("mapItemIcons") private mapIcons!: MarkerIcons;
	@MapLayers.State("filterChanged") private filterChanged: number;
	@MapLayers.Mutation("setAdvancedFilters") private setAdvancedFilters: (filters: AdvancedFilters) => void;
	@MapLayers.Mutation("resetState") private resetAdvancedFilters: () => void;
	@MapLayers.Mutation private setMapLayersItemType: any;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getPermissions") private permissions: UserPermissions;
	@Eventqueue.Getter("getActiveEvents") events!: FilteredEvent[];
	@Eventqueue.Action("fetchEventQueue") fetchEventQueue: any;

	@Getter public getUseImperialMeasurements: any;
	@Getter public getMapType: any;
	@Getter public getHideLabels: any;
	@Getter("getIsFieldOpsLicensed") private isFieldOpsLicenced: boolean;
	@Getter("getIsResponseLicensed") private isResponseLicenced: boolean;
	@Mutation public setMapType: any;
	@Getter("getMapKey") public mapKey: any;

	@FieldOpsStore.Action("loadOnlineAssets") private loadOnlineAssets: () => Promise<void>;
	@FieldOpsStore.Action("loadAssetTypes") private loadAssetTypes: () => Promise<void>;
	@FieldOpsStore.Mutation("setViewingAssetId") private setViewingAsset: (assetId: number) => void;
	@FieldOpsStore.State("activeSubscribers") private activeSubscribers: SubscriberExpiry[];

	@SiteMonitor.Action parkEvent: any;

	@SiteMonitor.Action fetchDeviceServerDetails: (deviceIds: number[]) => Promise<void>;
	@SiteMonitor.Action updateCurrentEventLocation: (latLng : string) => Promise<void>;
	@SiteMonitor.Getter("getIsCameraEnabled") private getIsCameraEnabled: (objectId: number) => boolean;
	@SiteMonitor.Getter("getApplianceOfflineNotification") private applianceOfflineNotification: (objectId: number) => string;
	@ExternalMapLayerStore.Getter("getExternalMapLayers") externalLayersList!: ExternalMapLayer[];
	@ExternalMapLayerStore.Action loadExternalMapLayers: () => Promise<void>;
	@ExternalMapLayerStore.Action updateExternalMapLayerVisibility: (externalMapLayer: ExternalMapLayer) => Promise<void>;
	@SchedulesStore.State("areaTimeZoneId") private areaTimeZoneId: number | null;

	@AreaNotes.Mutation("setAreaNoteGroupDetails") private setAreaNoteGroupDetails: ({ groupId, groupTitle }) => void;

	@MapStore.State((state: MapState) => state.clusterContextMenuResponse)
    public readonly clusterContextMenuResponse: ClusterContextMenuResponse | null;

	public created(): void {
		this.setVisibleMarkerKeysEventListener();
		this.setManualRaiseShown(false);
	}

	private async mounted(): Promise<void> {
		await this.loadMapLayersItemTypes();
		await this.init();

		if (this.getFeature(["Devices", "Appliances", "AppliancePolling", "Maps"])) {
			await this.startAppliancePolling();
		}
	}

	private destroy(): void {
		this.clearMap();
	}

	private beforeDestroy(): void {
		// Set destruction flag FIRST to stop all async operations
		this.isDestroyed = true;

		//Stop polling and clear timeouts
		this.stopAppliancePolling();

		clearTimeout(this.eventQueueTimer);
		if (this.centerPositionTimeout != null) {
			clearTimeout(this.centerPositionTimeout);
			this.centerPositionTimeout = null;
		}
		if (this.displayAssetItemsTimeout != null) {
			clearTimeout(this.displayAssetItemsTimeout);
			this.displayAssetItemsTimeout = null;
		}
		if (this.searchAutocompleteTimeout != null) {
			clearTimeout(this.searchAutocompleteTimeout);
			this.searchAutocompleteTimeout = null;
		}
		if (this.externalMapLayersTimeout != null) {
			clearTimeout(this.externalMapLayersTimeout);
			this.externalMapLayersTimeout = null;
		}

		try {
			try {
				if(this.regions){
					this.regions.forEach(m => {
						try {
							// Detach polygon from map
							m.poly.setMap(null);
							// Clear polygon paths to release memory
							if (m.poly.getPath) {
								try {
									m.poly.setPath([]);
								} catch(e) {
									// Silent fail
								}
							}
							// Remove label
							if(m.label) {
								try {
									m.label.setMap(null);
									m.label.onRemove();
								} catch(e) {
									console.error("Failed to remove label", e);
								}
							}
						} catch(e) {
							console.error("Failed to remove region", e);
						}
					});
					this.regions.clear();
					this.regions = null;
				}
			} catch(e) {
				console.error("Error clearing regions", e);
			}

			if(this.alarmStyle) {
				document.body.removeChild(this.alarmStyle);
			}

			// Clean up Google Maps event listeners
			if (this.mapEventListeners && this.mapEventListeners.length > 0) {
				this.mapEventListeners.forEach(listener => {
					if (listener && listener.remove) {
						listener.remove();
					}
				});
				this.mapEventListeners = [];
			}

			// Clean up search autocomplete listeners
			if (this.searchAutoComplete) {
				google.maps.event.clearInstanceListeners(this.searchAutoComplete);
				this.searchAutoComplete = null;
			}

			if(this.map) {
				this.map.reset();
			}

			this.setVisibleMarkerKeysResponse(null);
			this.resetAdvancedFilters();

			if(this.enhancedMapping) {
				this.enhancedMapping.dispose();
			}
			this.clearMap();
			if(this.eventMarker){
				// Clear callbacks to prevent memory leaks
				if (this.eventMarker.dragend) {
					this.eventMarker.dragend = null;
				}
				this.eventMarker.remove();
			}

			this.removeAdhocAlarmMarkers();

			if (this.clickCircle != null) {
				// Clear callback to prevent memory leak
				this.clickCircle.editted = null;
				this.clickCircle.remove();
				this.clickCircle = null;
			}

			this.clearAssetMarkers();

			if(this.alertMarkers) {
				this.clearAlertMarkers(this.alertMarkers);
			}

			if(this.regions){
				this.regions.forEach(m => {
					m.poly.setMap(null);
					if(m.label)
						m.label.onRemove();
					m = null;
				})
			}
		} finally {
			// Keep critical teardown idempotent and guaranteed even if an earlier cleanup step throws.
			this.mapMarkerManager?.resetMarkers();
			this.mapMarkerManager = null;

			this.mapInfoWindowService?.resetInfoWindows();
			this.mapInfoWindowService = null;
		}

		return;
	}

	@Watch("advancedFilters", { deep: true })
	private advancedFiltersUpdated(): void {
		if (this.filterChanged != null){
			if (this.filterChanged == -2){
				this.toggleRegions();
				return;
			}
			let filterChanged = this.enabledMapItemTypes.filter(t => t.mapLayerItemTypeId == this.filterChanged);

			if (filterChanged) {
				this.toggleVisibility(filterChanged[0]);
			}

			this.onMapLayerItemTypeAdvancedFilterChange();
		}
	}

	private get isMobileEventShareEnabled(): boolean {
		return this.getFeature(["Mobile", "FieldOps", "EventShare"]);
	}

	private get isAreaLocationEnabled(): boolean {
		return this.getFeature(["Areas", "ShowOnMap"]);
	}

	private get isTrackedAssetsEnabled(): boolean {
		return this.getFeature(["Mobile", "FieldOps", "TrackedAssets"]);
	}

	private get canViewCameraPreview(): boolean {
		var featureStatus = this.getFeature(["Map", "RestrictCameraMapView"]);
		return !featureStatus || (featureStatus && this.permissions.canViewCameraPreviewOnMap);
	}

	private closeCamera(cameraId: number){
		const camera = this.viewingCameras.find(x => x.id === cameraId);
		if(cameraId && camera)
			this.viewingCameras.remove(camera);
	}

	private get isSiteMonitorMode() :boolean{
		return this.enhancedMapMode === EnhancedMapMode.SiteMonitor;
	}

	private get isFieldOpsMode() :boolean {
		return this.enhancedMapMode === EnhancedMapMode.FieldOps;
	}

	private get isEventQueueMode() :boolean{
		return this.enhancedMapMode === EnhancedMapMode.EventQueue;
	}

	private get isEventSearchMode(): boolean {
		return this.enhancedMapMode === EnhancedMapMode.EventSearch;
	}

	private get isExternalMapLayersMode(): boolean {
		return this.enhancedMapMode === EnhancedMapMode.ExternalMapLayers;
	}

	private get isSiteMonitorOrEventSearchMode(): boolean {
		return this.isEventSearchMode || this.isSiteMonitorMode;
	}

	private async upLevel() {
		let higherFloors = this.elevationList.filter(elevation => elevation.elevationValue > this.elevation);
		if (higherFloors.length > 0) {
			this.elevation = higherFloors[0].elevationValue;
			await this.updateElevation();
		}
	}

	private async downLevel() {
		let lowerFloors = this.elevationList.filter(elevation => elevation.elevationValue < this.elevation);
		if (lowerFloors.length > 0) {
			this.elevation = lowerFloors[lowerFloors.length - 1].elevationValue;
			await this.updateElevation();
		}
	}

	private async gotoElevation(elevation: any) {
		this.elevation = elevation.elevationValue;
		this.displayElevation = elevation.name;
		await this.updateElevation();
	}

	private async updateElevation() {
		this.resetInfoWindow();
		this.clearMap(false);
		await this.loadInBounds(this.map!.mapBounds, (this.map!.currentViewState === "narrow" || (this.isFieldOpsMode && this.map!.zoom >= this.defaultMinimumZoomLevel)));
		await this.getItemsInCircle();
	}

	private showOverlayAlarm(alarm: any) {
		this.openAlarmOverlay = true;
		this.alarmToOpen = alarm;
	}

	private get overlayText(): string{
		return this.alarmToOpen ?
		`(${this.alarmToOpen.groupTitle} :
			${this.alarmToOpen.displayTitle ? this.alarmToOpen.displayTitle : this.alarmToOpen.eventTitle})` : ""
	}

	private async openAlarmInSiteMonitor(){
		var previousEvent = {...this.EventDetails}

		//If in eventQueue redirect, if fieldOps new window
		await this.handleEventQueueItem(
			this.alarmToOpen,
			false,
			this.isEventQueueMode ? false: this.isFieldOpsMode,
			"Picked up from Map")

		//If we had an active site monitor with an event which isn't the selected one park it
		if(previousEvent && previousEvent.eventID && previousEvent.eventID !== this.alarmToOpen.eventID){
			await this.parkEvent({
					eventId: previousEvent.eventID,
					hours: 0,
					minutes: 0,
					seconds: 0,
					parkNote: 'Switched alarm from map - placing back in queue',
					parkedByMap: true
				});
		}
		this.openAlarmOverlay = false;
	}

	private filterCondition(filter: AdvancedFilterOption, marker?: IMarker | MapLayerItem): boolean {
		//Assigns the relevant filter condition based on the filter type
		if(filter.filterType == "Basic"){
			return filter.filterCondition as boolean;
		} else if (filter.filterType == "Advanced"){
			return (filter.filterCondition as Function)(marker);
		}
	}

	private filterConditionWithAssetType(showAsset: boolean, asset: AssetMapLayerItem): boolean {
		//Checks the assetTypes array in the store to determine if marker should be visible
		if(!this.useAdvancedFilters){
			return showAsset;
		} else {
			let assetVisible = this.advancedFilters.assetTypes.find(ast => ast == asset.assetTypeId);

			if (assetVisible){
				return showAsset;
			} else {
				return false;
			}
		}
	}

	private filterConditionWithTrackedAsset(showAsset: boolean): boolean {
		if(!this.useAdvancedFilters){
			return showAsset;
		}

		if(!this.isTrackedAssetsEnabled){
			return false;
		} else {
			return showAsset;
		}
	}

	private async toggleBasicVisibility(mapItemType: MapLayerItemType): Promise<void> {
		//Basic filters' visibility need to be toggled here first before passing it on
		//Advanced filters' visibility is controlled beforehand so they go directly to the toggleVisibility method from the watcher
		var newMapItem = {
			...mapItemType,
			visible: !mapItemType.visible
		};

		await this.onMapLayerItemTypeBasicFilterChange(newMapItem);
		this.toggleVisibility(newMapItem);
	}

	private async toggleVisibility(mapItemType: MapLayerItemType): Promise<void> {
		if(!mapItemType) {
			return;
		}

		this.setMapLayersItemType({ id: mapItemType.mapLayerItemTypeId, data: mapItemType });

		if(mapItemType.mapLayerItemTypeId === MapLayerItemTypeIds.User || mapItemType.mapLayerItemTypeId === -1 || mapItemType.mapLayerItemTypeId === MapLayerItemTypeIds.TrackedAsset)
		{
			this.displayAssetItems();
		}

		if(mapItemType.mapLayerItemTypeId === MapLayerItemTypeIds.Alarm ){
			for (let eventRecordID in this.eventQueueMarkers) {
				this.eventQueueMarkers[eventRecordID].visible = this.useAdvancedFilters ?
					this.filterCondition(this.advancedFilters.alarm, this.eventQueueMarkers[eventRecordID]) :
					mapItemType.visible;
			}
		}

		if (this.markersByTypeAndObject[mapItemType.mapLayerItemTypeId] != null) {
			var ByObject = this.markersByTypeAndObject[mapItemType.mapLayerItemTypeId];

			for (var objectId in ByObject) {
				ByObject[objectId].forEach(marker => (marker.markerTypeVisible = mapItemType.visible));
			}
		}

		if (mapItemType.mapLayerItemTypeId == MapLayerItemTypeIds.Alarm) {
			for (let eventRecordID in this.eventAdHocAlarmMarkers) {
				this.eventAdHocAlarmMarkers[eventRecordID].visible = this.useAdvancedFilters ?
					this.filterCondition(this.advancedFilters.alarm, this.eventQueueMarkers[eventRecordID]) :
					mapItemType.visible;
			}

			for (var eventID in this.eventIcons) {
				this.eventIcons[eventID].forEach(eventMarker => {
					this.eventQueueMarkers[eventMarker.eventRecordID!].visible = this.useAdvancedFilters ?
						this.filterCondition(this.advancedFilters.alarm, this.eventQueueMarkers[eventMarker.eventRecordID!]) :
						mapItemType.visible;
				});
			}
		}

		await this.displayMapItems(this.mapData.items as MapLayerItem[]);
		this.displayAreaMarkers(this.mapData.areas as AreaMapDetails[]);
		this.resetInfoWindow();
	}

	private clearMap(clearEventMarkers: boolean = true) {
		this.mapMarkerManager.resetMarkers();

		for (var layerId in this.layersAdded) {
			let layer = this.layersAdded[layerId];

			layer.remove();
			delete this.layersAdded[layerId];
		}

		for (var itemId in this.itemsAdded) {
			let item = this.itemsAdded[itemId];

			item.state = "";
			if (this.markersByTypeAndObject[item.displayMapLayerItemTypeId]) {
				if (this.markersByTypeAndObject[item.displayMapLayerItemTypeId][item.objectId!]) {
					delete this.markersByTypeAndObject[item.displayMapLayerItemTypeId][item.objectId!];
				}
			}
			item.visible = false;
			item.remove();
			delete this.itemsAdded[itemId];
		}

		this.eventActiveItems = [];

		if (clearEventMarkers) {
			for (var adHocItemId in this.eventAdHocAlarmMarkers) {
				let item = this.eventAdHocAlarmMarkers[adHocItemId];

				item.state = "";
				if (this.eventAdHocAlarmMarkers[item.displayMapLayerItemTypeId]) {
					if (this.eventAdHocAlarmMarkers[item.displayMapLayerItemTypeId][item.objectId!]) {
						delete this.eventAdHocAlarmMarkers[item.displayMapLayerItemTypeId][item.objectId!];
					}
				}
				item.visible = false;
				item.remove();
				delete this.eventAdHocAlarmMarkers[itemId];
			}

			for (var eventID in this.eventIcons) {
				this.eventIcons[eventID].forEach(eventMarker => {
					delete this.eventQueueMarkers[eventMarker.eventRecordID!];
					eventMarker.visible = false;
					eventMarker.remove();
				});

				delete this.eventIcons[eventID];
			}

			if (this.eventMarker) {
				// Clear callbacks before removing
				if (this.eventMarker.dragend) {
					this.eventMarker.dragend = null;
				}
				this.eventMarker.visible = false;
				this.eventMarker.remove();
			}

			if (this.manualRaiseAlarmMarker) {
				// Clear callbacks before removing
				if (this.manualRaiseAlarmMarker.dragend) {
					this.manualRaiseAlarmMarker.dragend = null;
				}
				this.manualRaiseAlarmMarker.visible = false;
				this.manualRaiseAlarmMarker.remove();
			}
		}
	}

	@Watch("elevationList", { deep: true })
	private updateDisplayElevation() {
		if(this.displayElevation === "" && this.elevationList.length > 0) {
			this.displayElevation = this.elevationList[0].name;
		}
	}

	/**
	 * @summary Handler for when the info window options have been altered.
	*/
	@Watch("infoWindowOptions")
	private onInfoWindowOptions(): void {
		if ((this.isMapStackedItemsFeatureEnabled || this.isMapPopOverEnabled) && this.infoWindowOptions) {
			this.mapInfoWindowService?.setInfoWindows(this.infoWindowOptions);
		}
	}

	private MapLabel: any = null;

	private get isFieldOpsEnabled(): boolean {
		return this.getFeature(["Mobile", "FieldOps"]);
	}

	private async performLoadMapDataByBounds(bounds: MapBounds): Promise<boolean> {
		if (this.loadMapDataToken) {
			this.loadMapDataToken.cancel("Cancel load Map data");
		}

		this.loadMapDataToken = axios.CancelToken.source();

		try {
				await this.loadMapDataByBounds({ mapBounds: bounds, cancelToken: this.loadMapDataToken.token });
			} catch (e) {
				return false; //Return when past requests are cancelled
			}

		return true;
	}

	private async init() {
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);
		this.MapLabel = require('@/scripts/mapping/mapLabel').default;
		if (this.isFieldOpsEnabled) {
			await this.loadAssetTypes();
			await this.loadOnlineAssets();
		}

		let pos: LocationBounds = {
			location: { lat: 0, lng: 0 },
			viewport: null
		};

		if (this.eventLocation && this.isSiteMonitorOrEventSearchMode) {
			pos = await geoCoder.decodeLocation(this.eventLocation, this.mapKey);

			let elevationBounds = this.eventElevations;

			if (this.EventDetails.alarmElevation) {
				this.elevation = this.EventDetails.alarmElevation;
			} else if (elevationBounds.minElevation !== null && this.elevation <= elevationBounds.minElevation) {
				this.elevation = elevationBounds.minElevation;
			} else if (elevationBounds.maxElevation !== null && this.elevation <= elevationBounds.maxElevation) {
				this.elevation = elevationBounds.maxElevation;
			} else {
				this.elevation = elevationBounds.minElevation;
			}

			if (pos) {
				this.noMapData = false;
			} else {
				this.noMapData = true;
				pos = {
					location: { lat: 0, lng: 0 },
					viewport: null
				};
			}
		} else {
			this.noMapData = true;
		}

		this.mapCenter = pos.location;
		this.map = await this.enhancedMapping.createMap(
			this.$refs.mappingContainer,
			pos.location,
			1,
			false,
			this.isFieldOpsMode ? "roadmap" : this.getMapType,
			this.isFieldOpsMode ? darkMapStyle : getMapStyle(this.getMapType, this.getHideLabels),
			// Defaulted to an empty array, as CustomMapViewTypes are not currently implemented.
			[]
		);

		this.map.map.setOptions({
			zoomControl: true,
			mapTypeControl: true
		});

		this.setupMarkerClusterer();

		if (this.isMapClusteringFeatureEnabled) {
			this.mapMarkerClustererOptions = this.markerClustererOptions;
			this.mapMarkerClustererService = new MapMarkerClustererService(
				this.enhancedMapping,
				this.map,
				this.mapMarkerClustererOptions
			);
		}

		if ( this.isMapStackedItemsFeatureEnabled || this.isMapPopOverEnabled) {
			this.mapInfoWindowService = new MapInfoWindowService(this.enhancedMapping, this.map);

			this.mapEventListeners.push(this.map!.map.addListener("zoom_changed", () => this.resetInfoWindow()));
			this.mapEventListeners.push(this.map!.map.addListener("click", (event: google.maps.IconMouseEvent) => {
				if (event.placeId) {
					this.resetInfoWindow();
				}
			}));
		}

		this.mapMarkerManager = new MapMarkerManager(
			this.enhancedMapping,
			this.map,
			// This flag is related to local/dev level logging, should never be true in a prod environment.
			false,
			// CanFilterByType is related to the Enterprise Unified Map Component.
			// As such it should be defaulted to false, pending further implementation.
			false,
			this.isMapSetupElevationsEnabled,
			this.mapMarkerClustererService,
			this.markerServiceOptions
		);

		await this.displayAssetItems();

		this.map!.registerEvent("onClick", (latLong: ILatLng) => {
			// Check if click is within any region 
			this.checkRegionClick(new google.maps.LatLng(latLong.lat, latLong.lng));

			if(this.isFieldOpsMode) {
				this.$emit("setClickLocation", latLong.lat + " " + latLong.lng);
			}
			else if (this.addingAlarm && this.isEventQueueMode) {
				this.setManualRaiseAlarmMarkerPosition(latLong);
			}
		});

		if (pos.viewport) {
			this.setBounds({
				north: pos.viewport.northeast.lat,
				east: pos.viewport.northeast.lng,
				south: pos.viewport.southwest.lat,
				west: pos.viewport.southwest.lng
			});
		}

		this.map!.staticMapIcon = this.staticEventMapIcon;
		this.mapType = this.getMapType;

		this.map!.registerEvent("onMapTypeChanged", (mapType: string) => {
			if(this.isSiteMonitorOrEventSearchMode)
				this.setActivity();

			this.mapType = mapType;
			this.setMapType(mapType);

			this.map!.map.setOptions({
				styles: getMapStyle(this.getMapType, this.getHideLabels)
			});
		});

		this.map!.registerEvent("onViewportChangedDelayed", () => {
			if(this.isSiteMonitorOrEventSearchMode)
				this.setActivity();

			if (this.map!.currentViewState === "narrow" || !this.isSiteMonitorOrEventSearchMode) {
				if (this.map!.zoom >= this.defaultMinimumZoomLevel) {
					this.loadInBounds(this.map!.mapBounds, true);
				} else {
					if(this.isAreaLocationEnabled) {
						this.mapMarkerManager.resetMarkers();
						this.getAreaMarkers();
					}

					if(this.isSituationalAwarenessExternalAlertEnabled){
						this.displayAlertsWithLocation();
					}
				}
			} else if (this.isSiteMonitorMode && this.isAreaLocationEnabled) {
				this.mapMarkerManager.resetMarkers();
				this.getAreaMarkers();
			} else {
				this.mapMarkerManager.resetMarkers();
			}
		});

		this.map!.registerEvent("onDisplayTypeChanged", (data: any) => {
			if(this.isSiteMonitorOrEventSearchMode)
				this.setActivity();

			let layersVisible = this.isSiteMonitorOrEventSearchMode
				? this.map!.currentViewState === "narrow"
				: this.map!.zoom >= this.defaultMinimumZoomLevel;

			for (var layerId in this.layersAdded) {
				this.layersAdded[layerId].visible = layersVisible;
			}
		});

		this.AdhocLocationEventRecords.forEach((record: any) => {
			this.createAdHocMarker(record);
		});

		//Load the event queue if we aren't on site monitor mode -- for now
		await this.fetchEventQueue();
		await this.updateEventMarkers();

		//If we aren't using the eventQueue trigger a reload of the eventQueue markers every 5 seconds
		if(!this.isEventQueueMode){
			const poll = () => {
				// Check if component is being destroyed before creating new timer
				if (this.isDestroyed) {
					return;
				}
				
				this.eventQueueTimer = setTimeout(async () => {
					// Double-check destruction flag
					if (this.isDestroyed) {
						return;
					}
					
					try {
						await this.updateEventMarkers();
						if (!this.isDestroyed) {
							poll();
						}
					} catch(e) {
						if (!this.isDestroyed) {
							clearTimeout(this.eventQueueTimer);
							this.eventQueueTimer = setTimeout(() => {
								if (!this.isDestroyed) {
									poll();
								}
							}, 5000);
						}
					}
				}, 5000);
			};
			poll();
		}

		await this.$nextTick();
		if (this.isSiteMonitorOrEventSearchMode && this.EventDetails) {
			this.onEventLocationChanged();
		}

		if(this.isAreaRegionsEnabled)
			this.loadRegions()
	}

	public async centerOnPosition(position: any, zoom:number = 19, keepZoom: boolean = false){
		if (this.isDestroyed) {
			return;
		}
		
		// Clear any existing timeout before creating a new one
		if (this.centerPositionTimeout != null) {
			clearTimeout(this.centerPositionTimeout);
			this.centerPositionTimeout = null;
		}
		
		if(!this.map){
			this.centerPositionTimeout = setTimeout(() => {
				this.centerPositionTimeout = null;
				if (!this.isDestroyed) {
					this.centerOnPosition(position, zoom, keepZoom);
				}
			}, 50);
			return;
		}

		if(this.map && position){
			this.map.map.panTo(position);
			if (this.map.map.getZoom() < zoom && !keepZoom) {
				this.map.map.setZoom(zoom);
			}
		}
	}

	private get isMapSetupElevationsEnabled(): boolean {
		return this.getFeature(["MapSetup", "Elevations"]);
	}

	private resetInfoWindow(): void {
		this.resetInfoWindows(this.isMapStackedItemsFeatureEnabled, this.isMapPopOverEnabled);
	}

	private get isOfflineCamerasEnabled(): boolean {
		return this.getFeature(["Map", "Status", "OfflineCameras"]);
	}

	// ----------------------------------------------------- Global Logic  ---------------------------------------------------------------------------------

	// ----------------------------------------------------- Site Monitor Logic  ----------------------------------------------------------------------------

	@Prop({ default: () => true })
	private showCircle: boolean;

	private clickCircleEventID: number = 0;
	private clickCircle: ICircle | null = null;
	private eventActiveItems = new Array<IMarker>();
	private eventAdHocAlarmMarkers = new ItemMap();
	private eventMarker: any = null
	public readonly activeIconType: IconType = "active";
	public readonly inactiveIconType: IconType = "inactive";
	public readonly brokenIconType: IconType = "broken";
	private displayItemsInCircle: DisplayItem[] = [];
	private activeMapItems = new Array<ActiveMapItem>();
	private activeMapLayerItemIds = new Set();
	private pendingEventMarkerPos: ILatLng = null;
	private originalEventLocation: ILatLng = null;
	private displayItems: DisplayItem[] = [];

	@SiteMonitor.Getter("getEventDetails") private EventDetails: EventDetails;
	@SiteMonitor.State private AdhocLocationEventRecords!: any[];
	@SiteMonitor.Getter("getEventLocationLastSet") private eventLocationLastSet: number;
	@SiteMonitor.Getter("getActiveResponseIDs") private activeResponseIDs: any;
	@SiteMonitor.Mutation private setActiveMapItems: any;
	@SiteMonitor.Mutation private setActiveMapItemsRequired: any;
	@SiteMonitor.Mutation private setMapCircleCenter: any;
	@SiteMonitor.Mutation private setActivity;
	@SiteMonitor.Mutation private updateFirstEventRecordLocation: (location: string) => void;
	@SMCameras.Mutation private setAwaitingCamera: any;
	@SMCameras.Mutation private clearDeviceControllerCameras: any;
	@SiteMonitor.State private goToLocation: string;

	@Watch("goToLocation")
	private async updateLocation(): Promise<void> {
		if(!this.goToLocation){
			return;
		}
		let gpsLocation = await geoCoder.decodeLocation(this.goToLocation, this.mapKey);

		if (gpsLocation) {
			this.mapCenter = gpsLocation.location;

			if (this.showCircle)
			{
				this.loadRadius(this.mapCenter);
			}

			if(gpsLocation.viewport){
				this.setBounds({
					north: gpsLocation.viewport.northeast.lat,
					east: gpsLocation.viewport.northeast.lng,
					south: gpsLocation.viewport.southwest.lat,
					west: gpsLocation.viewport.southwest.lng
				});
			}
			this.map.center = this.mapCenter;
			this.map.zoom = this.zoomLevel;
			this.noMapData = false;
		}
	}

	private setElevationToDefault(): void
	{
		let currentElevation = this.elevationList ? this.elevationList[0]: null;
		if (currentElevation)
		{
			this.elevation = currentElevation.elevationValue;
			this.displayElevation = currentElevation.name;
		}
		else
		{
			this.elevation = null;
			this.displayElevation = null;
		}
	}

	//Make this generic
	private async loadInBounds(mapBounds: any, layersVisible: boolean) {
		// Early exit if component is being destroyed
		if (this.isDestroyed) {
			return;
		}
		
		if (this.layersVisible != layersVisible) {
			for (var eventrecordId in this.eventAdHocAlarmMarkers) {
				this.eventAdHocAlarmMarkers[eventrecordId].visible = layersVisible;
			}
		}
		this.layersVisible = layersVisible;

		let bounds = {
			north: mapBounds.north,
			south: mapBounds.south,
			east: mapBounds.east,
			west: mapBounds.west,
			excludeLayerImage: true
		} as MapBounds;

		bounds.elevation = this.elevation ? this.elevation : 0;

		const loadSucceeded =  await this.performLoadMapDataByBounds(bounds);

		// Check if destroyed during async operation
		if (this.isDestroyed) {
			return;
		}

		// mapData is not updated, no need for further processing.
		if(!loadSucceeded) {
			return;
		}

		const elevations = this.mapData.elevations.slice();
		this.elevationList = elevations.sort(function(a, b) {
			return a.elevationValue - b.elevationValue;
		});

		if (this.elevation != null && this.elevation != undefined) {
			let currentElevation = elevations.find(
				(elevationFloor: any) => elevationFloor.elevationValue == this.elevation
			);
			if (currentElevation)
			{
				this.displayElevation = currentElevation.name;
			}
			else
			{
				this.setElevationToDefault();
			}
		} else if (elevations.length > 0) {
			this.setElevationToDefault()
		}
		this.displayLayers(this.mapData.layers as MapLayer[]);

		if (this.isSiteMonitorMode || this.isEventSearchMode) {
			await this.setMarkerByEventDetails();
		}

		await this.displayMapItems(this.mapData.items as MapLayerItem[]);

		if (this.showCircle && this.isSiteMonitorMode) {
			if (this.clickCircle === null || this.clickCircleEventID !== this.EventDetails.eventID) {
				this.clickCircleEventID = this.EventDetails.eventID;
				this.loadRadius(this.mapCenter);
			}
		}

		if (this.isAreaLocationEnabled) {
			this.displayAreaMarkers(this.mapData.areas as AreaMapDetails[]);
		}
	}

	private async goToEvent(){
		var pos = await geoCoder.decodeLocation(this.eventLocation, this.mapKey);
		if(!pos)
			return;

		this.centerOnPosition(pos.location);
		if(this.showCircle){
			this.loadRadius(pos.location);
		}
	}

	private loadRadius(position: any) {
		if(!this.isSiteMonitorOrEventSearchMode)
			return;

		if (this.clickCircle != null) {
			// Clear callback before removing
			this.clickCircle.editted = null;
			this.clickCircle.remove();
			this.clickCircle = null;
		}

		this.clickCircle = this.enhancedMapping.createCircle(
			this.map!,
			"#0d87e9",
			0.8,
			2,
			"#0d87e9",
			0.05,
			position,
			this.nearbyEventCameraRadius,
			this.allowSpotlightScaling
		);

		this.clickCircle.editted = latLng => this.getItemsInCircle();
		this.getItemsInCircle();
	}

	private get formattedEventsByEventId() {
		return this.events && keyBy(this.events, 'eventID')
	}

	private get eventLocation() {
		if (!this.EventDetails)
			return "";

		return this.EventDetails.latLong;
	}

	private get allowSpotlightScaling() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map", "SpotlightScaling"]);
	}

	// Feature flag checker for toggle maps feature.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleEventQueue"]) && this.showMapToggle;
	}

	private get nearbyEventCameraRadius() {
		if (!this.EventDetails ||
			!this.EventDetails.cameraNearbyRadius ||
			this.EventDetails.cameraNearbyRadius < 1)  {
			return 30;
		}
		return this.EventDetails.cameraNearbyRadius;
	}

	private get staticEventMapIcon(): boolean {
		if (!this.EventDetails ||
			!this.EventDetails.staticMapIcon)  {
			return false;
		}
		return this.EventDetails.staticMapIcon;
	}

	private get eventElevations() {
		var details = this.EventDetails;

		if (!details) {
			return {
				minElevation: null,
				maxElevation: null
			};
		}

		return {
			minElevation: details.minElevation,
			maxElevation: details.maxElevation
		};
	}

	@Watch("eventLocationLastSet")
	private async onEventLocationChanged() {
		if(!this.isSiteMonitorOrEventSearchMode) {
			return;
		}

		if (this.clusterMarkerClicked) {
			this.clusterMarkerClicked = false;
			return;
		}

		if (this.map && this.EventDetails) {
			let elevationBounds = this.eventElevations;

			if (this.EventDetails.alarmElevation) {
				this.elevation = this.EventDetails.alarmElevation;
			} else if (!elevationBounds.minElevation && this.elevation <= elevationBounds.minElevation) {
				this.elevation = elevationBounds.minElevation;
			} else if (!elevationBounds.maxElevation && this.elevation <= elevationBounds.maxElevation) {
				this.elevation = elevationBounds.maxElevation;
			} else {
				this.elevation = elevationBounds.minElevation;
			}

			if (!this.eventLocation) {
				this.noMapData = true;
			} else {
				let gpsLocation = await geoCoder.decodeLocation(this.eventLocation, this.mapKey);

				if (gpsLocation) {
					this.mapCenter = gpsLocation.location;

					if (this.showCircle) {
						this.loadRadius(this.mapCenter);
					}

					if(gpsLocation.viewport){
						this.setBounds({
							north: gpsLocation.viewport.northeast.lat,
							east: gpsLocation.viewport.northeast.lng,
							south: gpsLocation.viewport.southwest.lat,
							west: gpsLocation.viewport.southwest.lng
						});
					}
					this.map.center = this.mapCenter;
					this.map.zoom = this.zoomLevel;
					this.noMapData = false;
				} else {
					this.noMapData = true;
				}
			}
		}
	}

	private async getItemsInCircle(): Promise<void> {
		if(!this.isSiteMonitorMode)
			return;

		this.clearDeviceControllerCameras();

		const circleCenter = this.clickCircle!.center;

		this.setMapCircleCenter(circleCenter.lat + " " + circleCenter.lng);

		// If feature is enabled add handles to the circle
		if (this.clickCircle && this.allowSpotlightScaling) {
			this.clickCircle!.editable = true;
		}

		// Redraw the map markers differently if there is a circle
		await this.displayMapItems(this.mapData.items as MapLayerItem[]);
		this.displayAreaMarkers(this.mapData.areas as AreaMapDetails[]);
		return;
	}

	/**
	 * @summary Clears the collections that calculate circle items.
	 */
	private clearCircleItems(): void {
		this.displayItemsInCircle = [];
		this.activeMapItems = [];
		this.activeMapLayerItemIds.clear();
	}

	/**
	 * @summary Calculates all items in the circle and updates the active map items.
	 */
	private calculateAllItemsInCircle(): void {
		this.eventActiveItems = this.displayItemsInCircle;

		// Sort the active map items by distance
		this.activeMapItems = this.activeMapItems.sort((a, b) => {
			return a.distance - b.distance;
		});

		// Remove duplicates by itemType and objectId
		let uniqueDistances = [];
		this.activeMapItems.forEach(distance => {
			if (
				!uniqueDistances.find(
					item => item.itemType == distance.itemType && item.objectId == distance.objectId
				)
			) {
				uniqueDistances.push(distance);
			}
		});

		this.activeMapItems = uniqueDistances;

		if (this.getUseImperialMeasurements) {
			this.activeMapItems.forEach(distance => {
				distance.distance = geoCoder.metersToFeet(distance.distance);
				distance.imperial = true;
			});
		}

		this.setActiveMapItems(this.activeMapItems);
		this.setActiveMapItemsRequired(false);
	}

	/**
	 * @summary Processes a single displayItem in the circle and updates the active map items and icon for that item.
	 */
	private processItemForCircle(item: DisplayItem): void {
		if (item.mapLayerItemTypeId === MapLayerItemTypeIds.Alarm) {
			return;
		}

		if (this.isItemInsideCircle(item)) {
			item.state.iconType = item.state.iconType == this.brokenIconType ? this.brokenIconType : this.activeIconType;
		    item.key = Md5.hashStr(JSON.stringify(item));

			this.activeMapItems.push({
				distance: this.getDistanceToCircleCenter(item),
				itemType: item.typeId,
				title: item.title,
				objectId: item.objectId,
				imperial: false
			});

			this.displayItemsInCircle.push(item);
			this.activeMapLayerItemIds.add(item.mapLayerItemId);
		}
		else {
			// Every item that's not active (not in the circle)
			if (!this.activeMapLayerItemIds.has(item.mapLayerItemId)) {
				// Change the icon to inactive or broken
				item.state.iconType = item.state.iconType == this.brokenIconType ? this.brokenIconType : this.inactiveIconType;
				item.key = Md5.hashStr(JSON.stringify(item));
			}
		}
	}

	/**
	 * @summary Checks if the item is inside the circle.
	 */
	private isItemInsideCircle(item: DisplayItem): boolean {
		const circleRadius = this.clickCircle!.radius;
		const distance = this.getDistanceToCircleCenter(item);
		return distance <= circleRadius;
	}

	/**
	 * @summary Calculates the distance from the item to the circle center.
	 */
	private getDistanceToCircleCenter(item: DisplayItem): number {
		const circleCenter = this.clickCircle!.center;
		return geoCoder.getDistance(circleCenter, {
			lat: item.position.lat,
			lng: item.position.lng
		});
	}

	@Watch("adhocLocationEventRecords")
	private onAdhocLocationEventRecordsChange(records: any[]) {
		if(this.isFieldOpsMode || this.isEventQueueMode)
			return;

		var recordsAdded = [];
		if (this.map) {
			records.forEach(record => {
				if (!this.eventAdHocAlarmMarkers[record.eventRecordID]) {
					this.createAdHocMarker(record);
					recordsAdded.push(record.eventRecordID);
				}
			});
		}

		this.removeAdhocAlarmMarkers(recordsAdded);
	}

	private removeAdhocAlarmMarkers(doNotRemove: string[] = null) {
		for (let eventRecordId in this.eventAdHocAlarmMarkers) {
			if(!doNotRemove || !doNotRemove.includes(eventRecordId))
			{
				// if this object isn't null and it has the remove function
				if (this.eventAdHocAlarmMarkers[eventRecordId] !== null
					&& typeof this.eventAdHocAlarmMarkers[eventRecordId].remove === "function") {
					this.eventAdHocAlarmMarkers[eventRecordId].remove();
				}
				this.eventAdHocAlarmMarkers[eventRecordId] = null;
			}

		}
	}

	private createAdHocMarker(record: any) {
		if (this.isEventQueueMode && this.isFieldOpsMode) {
			return;
		}

		if (this.eventAdHocAlarmMarkers[record.eventRecordID] && typeof this.eventAdHocAlarmMarkers[record.eventRecordID].remove === "function") {
			this.eventAdHocAlarmMarkers[record.eventRecordID].remove();
		}

		var location = record.location.split(",");
		this.eventAdHocAlarmMarkers[record.eventRecordID] = this.enhancedMapping.createMarker(
			Md5.hashStr(JSON.stringify(record)),
			this.map!,
			null,
			record.eventRecordID.toString(),
			MapLayerItemTypeIds.Alarm,
			record.objectId,
			record.details,
			convertStringToLatLng(location[0]),
			null,
			null,
			null,
			true
		);

		this.eventAdHocAlarmMarkers[record.eventRecordID].state = record.awaitingAcknowledgement ? "active" : "processing";
		this.eventAdHocAlarmMarkers[record.eventRecordID].visible = this.useAdvancedFilters ?
			this.filterCondition(this.advancedFilters.alarm, this.eventAdHocAlarmMarkers[record.eventRecordID]) :
			true;
	}

	private getEventMarkerProcessingState(): string {
		const inProcessing = get(this.formattedEventsByEventId, `[${this.EventDetails.eventID}].inProcessing`);
		const isProcessing = inProcessing || this.EventDetails.unParkAt !== null;

		return isProcessing ? "processing" : "active";
	}

	private async setMarkerByEventDetails() {
		const markerLocation = await geoCoder.decodeLocation(this.eventLocation, this.mapKey);
		if (this.eventMarker) {
			return;
		}

		const eventDetails: FilteredEvent = this.events.find(e => e.eventID === this.EventDetails.eventID);

		let createdDisplayItem = this.createMarker({
			key: Md5.hashStr(JSON.stringify(this.EventDetails)),
			title: this.EventDetails.eventTitle,
			mapLayerItemId: this.EventDetails.eventID,
			objectId: eventDetails?.responseID,
			eventRecordId: null,
			typeId: MapLayerItemTypeIds.Alarm,
			position: markerLocation.location,
		} as DisplayItem);

		let marker = this.mapMarkerManager.createMarker(createdDisplayItem);

		marker.state = this.getEventMarkerProcessingState();
		if(this.canMoveEventLocation){
			marker.isDraggable = true;
			marker.dragend = (latLong: ILatLng) => {
				marker.state = "pendingupdate";
				this.pendingEventMarkerPos = latLong;
			};

		}
		this.originalEventLocation = markerLocation.location;
		this.eventMarker = marker;
	}

	/**
	 * @summary Displays the alarms with locations from the eventqueue
	 */
	private async displayMapItems(mapItems: MapLayerItem[]): Promise<void> {
		if (this.map?.zoom <= this.defaultMinimumZoomLevel) {
			return;
		}

		let mapItemIDs: string[] = [];
		let markerTypeVisibilityByID = new LayerTypeMap();

		if (mapItems?.length > 0) {
			// exclude the Alarm, Asset and User map layer items from the map items
			mapItems = mapItems.filter(mt =>
				mt.mapLayerItemTypeId != MapLayerItemTypeIds.Asset &&
				mt.mapLayerItemTypeId != MapLayerItemTypeIds.User &&
				mt.mapLayerItemTypeId != MapLayerItemTypeIds.Alarm);

			let deviceIdsForIsEnabled: number[] =
				mapItems.filter(item => !!item.objectId && item.mapLayerItemTypeId === MapLayerItemTypeIds.Camera)
					.map(item => {
						return item.objectId;
					});

			await this.fetchDeviceServerDetails(deviceIdsForIsEnabled);

			if (this.isOfflineCamerasEnabled) {
				// Remove camera items that should be hidden based on filters
				mapItems = mapItems.filter(mapitem => {
					if (mapitem.mapLayerItemTypeId === MapLayerItemTypeIds.Camera) {
						return this.useAdvancedFilters
							? this.filterCondition(this.advancedFilters.camera, mapitem)
							: true;
					}
					return true;
				});
			}
		}

		this.displayItems = [];
		this.clearCircleItems();

		mapItems?.forEach(mapItem => {
			// Map Item Type is not selected in the filter.
			if (this.hiddenMapLayerItemTypes.has(mapItem.mapLayerItemTypeId)) {
				return;
			}

			let mapLayerItemId = mapItem.mapLayerItemId;

			if ((mapItem.minElevation == null || mapItem.minElevation! <= this.elevation) &&
				(mapItem.maxElevation == null || mapItem.maxElevation! >= this.elevation)
			) {
				mapItemIDs.push(mapItem.mapLayerItemId.toString());

				// we don't want to display the map layer item associated with an alarm if there is an alarm already active for that map layer item,
				// as this will prevent the alarm icon from being interactive.
				let isActiveAlarmMapLayerItem = this.isEventQueueMode && this.events.some(e => e.location === mapItem.latLong);

				if (!isActiveAlarmMapLayerItem) {
					let displayItem: DisplayItem = this.createMarker({
						key: Md5.hashStr(JSON.stringify(mapItem)),
						typeId: mapItem.mapLayerItemTypeId,
						title: mapItem.title,
						position: convertStringToLatLng(mapItem.latLong),
						mapLayerItemId: mapLayerItemId,
						regionPath: mapItem.regionPath,
						minElevation: mapItem.minElevation,
						maxElevation: mapItem.maxElevation,
						objectId: mapItem.objectId,
						groupId: mapItem.groupId,
						state: {
							clickable: false,
							draggable: false,
							noCluster: false, // default, Cluster the Display item.
							iconType: "active"
						}
					});

					if (mapItem.objectId != null) {
						if (this.markersByTypeAndObject[displayItem.typeId] == null) {
							this.markersByTypeAndObject[displayItem.typeId] = new ItemObjectMap();
						}

						if (!this.markersByTypeAndObject[displayItem.typeId][mapItem.objectId!]) {
							this.markersByTypeAndObject[displayItem.typeId][mapItem.objectId!] = [];
						}

						// Set up OnClick for the Camera marker.
						if (displayItem.typeId === MapLayerItemTypeEnum.Camera) {
							displayItem.state.iconType = "active";

							if (this.getIsCameraEnabled(mapItem.objectId)) {
								displayItem.state.clickable = true;

								// If the last interaction is not healthy, set the icon type to broken.
								if (this.isOfflineCamerasEnabled && mapItem.lastInteractionHealthy === false) {
									displayItem.state.iconType = "broken";
								}

								if (this.onClickEnabledForMapItem) {
									this.addClickForCameraMapItem(displayItem);
								}

							} else {
								displayItem.state.iconType = "broken";
								displayItem.state.clickable = false;
							}
						}

						// if Clustering is not enabled for this marker item type,
						// disable clustering for the displayItem.
						if (!this.isItemClusterable(displayItem.typeId)) {
							displayItem.state.noCluster = true;
						}

						if (this.isMapPopOverEnabled) {
							displayItem.state.clickable = true;
						}

						if (this.clickCircle) {
							this.processItemForCircle(displayItem);
						}

						this.displayItems.push(displayItem);
					}
				}
			}
		});

		if (this.clickCircle) {
			this.calculateAllItemsInCircle();
		}

		this.mapMarkerManager.addMarkers(this.displayItems);

		// Once rendered, set the icon type for each marker (they won't update if the iconType has changed).
		this.displayItems.forEach(item => {
			this.mapMarkerManager.setIconType(item.key, item.state.iconType);
		});

		this.mapMarkerManager.getMarkersMap.forEach(marker => {
			// If item already exists, skip to avoid duplicates.
			if (this.itemsAdded[marker.mapLayerItemId]) {
				return;
			}

			let itemTypeId = marker.mapLayerItemTypeId as number;
			this.itemsAdded[marker.mapLayerItemId] = marker as IMarker;

			let selectedItemType = MapLayerItemTypeIds[itemTypeId].toString().toLowerCase();
			if (markerTypeVisibilityByID[itemTypeId] != null) {
				this.itemsAdded[marker.mapLayerItemId].markerTypeVisible =
					this.useAdvancedFilters ? this.advancedFilters[selectedItemType].filterCondition :
						markerTypeVisibilityByID[marker.mapLayerItemId];
			}

			if(this.useAdvancedFilters ? this.advancedFilters[selectedItemType].filterCondition :
				markerTypeVisibilityByID[itemTypeId]) {
				return;
			}
		});

		let assetIds = this.assetAndUsersItems ? this.assetAndUsersItems.map(au => au.mapLayerItemId) : [];

		for (const itemId in this.itemsAdded) {
			let item = this.itemsAdded[itemId];
			if (mapItemIDs.indexOf(itemId) === -1) {
				//Don't delete any asset items as this will be handled separately by the cleanup of old assets in the displayAssetItems() method
				if(assetIds && assetIds.length > 0 && assetIds.includes(parseInt(itemId))){
					continue;
				}

				item.state = "";

				if (this.markersByTypeAndObject[item.displayMapLayerItemTypeId]) {
					if (this.markersByTypeAndObject[item.displayMapLayerItemTypeId][item.objectId!]) {
						delete this.markersByTypeAndObject[item.displayMapLayerItemTypeId][item.objectId!];
					}
				}

				item.remove();
				delete this.itemsAdded[itemId];
			}
		}
	}

	private get onClickEnabledForMapItem(): boolean {
		return !this.isEventEditMode
			&& (this.isSiteMonitorMode || ((this.isEventQueueMode || this.isFieldOpsMode) && this.canViewCameraPreview));
	}

	private displayLayers(layers: MapLayer[]) {
		const itemLayerIDs: string[] = [];

		layers.forEach(layer => {
			const elevation = this.elevation;

			let isElevated =
				(layer.minElevation === null || layer.minElevation! <= elevation) &&
				(layer.maxElevation === null || layer.maxElevation! >= elevation);


			if (isElevated) {
				itemLayerIDs.push(layer.mapLayerId.toString());
				if (this.layersAdded[layer.mapLayerId] == undefined) {
					this.layersAdded[layer.mapLayerId] = this.enhancedMapping.createOverlay(
						this.map!,
						layer.mapLayerId,
						layer.title,
						stringToBounds(layer.ne, layer.sw), // boundaries
						layer.rotation, // rotation
						axiosInstance.defaults.baseURL + "/MapLayers/Image/" + layer.mapLayerId.toString(), //imageUrl
						layer.minElevation, // minElevation,
						layer.maxElevation, // maxElevation
						this.layersVisible // , visible
					);
				}
			}
		});

		for (var layerId in this.layersAdded) {
			if (itemLayerIDs.indexOf(layerId) === -1) {
				let layer = this.layersAdded[layerId];

				layer.remove();
				delete this.layersAdded[layerId];
			}
		}
	}

	private addClickForCameraMapItem(displayItem: DisplayItem): void {
		displayItem.state.click = () => {
			if (this.getIsCameraEnabled(displayItem.objectId)) {
				this.$emit("onCameraIconClick");
				if (this.isSiteMonitorMode) {
					const clusteredMarker = this.clusterContextMenuResponse?.markers
						.find((item: MarkerMetadata) =>
							displayItem.mapLayerItemId === item.mapLayerItemId);

					if (clusteredMarker) {
						this.clusterMarkerClicked = true;
					}

					this.setAwaitingCamera({
						objectId: displayItem.objectId, // DeviceId for the camera
						title: displayItem.title
					});
				} else if (!this.isEventSearchMode && !this.isExternalMapLayersMode) {
					const camera: MapCameraDevice = {
						id: displayItem.objectId,
						title: displayItem.title
					}

					if (!this.viewingCameras.find(x => x === camera)) {
						this.viewingCameras.push(camera);
					}
				}
			} else {
				displayItem.state.iconType = "broken";
				displayItem.setClickable(false);
			}
		};
	}

	private async displayAreaMarkers(mapDetails: AreaMapDetails[]): Promise<void> {
		if (this.hiddenMapLayerItemTypes.has(MapLayerItemTypeIds.Area)) {
			return;
		}

		let areaMarkersToRemove = [...this.areaMarkers];

		// get location of event if in site monitor
		let pos;
		if (this.isSiteMonitorMode) {
			pos = await geoCoder.decodeLocation(this.eventLocation, this.mapKey);
		}

		try {
			mapDetails.forEach(details => {
				if (!details.markerLocation) {
					return;
				}

				// check for event markers (alarms etc) - don't create area marker if event marker exists with the same lat lng
				for(const itemId in this.eventQueueMarkers){
					const item = this.eventQueueMarkers[itemId];
					if (item && item.position.lat.toString() == details.markerLocation.split(" ")[0] && item.position.lng.toString()== details.markerLocation.split(" ")[1]){
						return;
					}
				}

				// if we're in site monitor, don't create an area marker if it has the same lat lng as the map centre (selected event location)
				if (this.isSiteMonitorMode && (pos?.location?.lat == details.markerLocation.split(" ")[0] && pos?.location?.lng == details.markerLocation.split(" ")[1])){
					return;
				}

				// Item latLng is formatted here to be used to extend bounds
				const latLng: ILatLng = convertStringToLatLng(details.markerLocation!);
				if(this.isFieldOpsMode && this.computeDefaultView.areas == false){
					this.extendDefaultViewBounds(latLng);
				}

				//check if areaMarker already exists
				const areaMarkerExists = this.areaMarkers.find(m => m.groupId == details.groupId);
				if (areaMarkerExists) {
					areaMarkersToRemove = areaMarkersToRemove.filter(m => m.groupId != details.groupId);
				}

				// displayMapItems() clears the markers that are not in the mapItems list
				// add the area marker to the mapMarkerManager
				// todo: DisplayItems for MapItem and Area should be merged into one list and passed to the mapMarkerManager.
				const areaDisplayItem: DisplayItem = this.getDisplayItemForAreaMapDetails(details, latLng);
				this.mapMarkerManager.addMarker(areaDisplayItem);
			});
		} catch (e) {
			console.error("Failed to create an area marker", e);
		}

		const markers: SVMarker[] = Array.from(this.mapMarkerManager.getMarkersMap.values());
		this.areaMarkers = markers.filter(m => m.mapLayerItemTypeId == MapLayerItemTypeIds.Area);

		const groupsToRemove: number[] = areaMarkersToRemove.map<number>(m => m.groupId);
		this.areaMarkers = this.areaMarkers.filter(a => !groupsToRemove.includes(a.groupId));

		this.clearAreaMarkers(areaMarkersToRemove);

		try {
			if (this.isMapClusteringAllFeatureEnabled) {
				this.mapMarkerManager.performClustering();
			}
		} catch (e) {
			console.error("Failed to perform clustering", e);
		}

		// Mark the areas complete so the view can be calculated
		this.setAreaComputeDefaultView(true);
	}

	private getDisplayItemForAreaMapDetails(details: AreaMapDetails, latLng: ILatLng): DisplayItem {
		const areaDisplayItem: DisplayItem = {
			key: Md5.hashStr(JSON.stringify(details)),
			typeId: MapLayerItemTypeIds.Area,
			title: details.title,
			position: latLng,
			mapLayerItemId: null,
			regionPath: null,
			minElevation: null,
			maxElevation: null,
			objectId: null,
			groupId: details.groupId,
			state: {
				clickable: true,
				draggable: false,
				noCluster: !this.isItemClusterable(MapLayerItemTypeIds.Area),
				iconType: "active"
			}
		};

		areaDisplayItem.state.click = () => {
			this.areaMarkerOnClick(areaDisplayItem);
		};

		return areaDisplayItem;
	}

	private clearAreaMarkers(markers: SVMarker[]): void {
		try {
			const keysToRemove = markers.map(m => m.key);
			this.mapMarkerManager.removeMarkers(keysToRemove);
		}
		catch (e) {
			console.error("Failed to clear area markers", e);
		}
	}

	private areaMarkerOnClick(selectedArea: DisplayItem): void
	{
		this.selectedArea = selectedArea;

		this.setAreaNoteGroupDetails({
			groupId: selectedArea.groupId,
			groupTitle: selectedArea.title,
		});

		this.$nextTick(() => {
			this.$bvModal.show("modal-area-details");
		});
	}

	private get enabledMapItemTypes(): MapLayerItemType[] {
		if(!this.mapItemTypes || this.mapItemTypes.length < 1){
			return []
		}

		let itemList = [...this.mapItemTypes];

		if(!this.isAreaLocationEnabled){
			itemList = itemList.filter(t => t.mapLayerItemTypeId != MapLayerItemTypeIds.Area);
		}

		if(!this.isTrackedAssetsEnabled){
			itemList = itemList.filter(t => t.mapLayerItemTypeId != MapLayerItemTypeIds.TrackedAsset);
		}

		return itemList;
	}

	private getMapItemTitle(mapItem: MapLayerItem): string {
		if(mapItem.mapLayerItemTypeId === MapLayerItemTypeIds.Camera) {
			if (this.getIsCameraEnabled(mapItem.objectId)) {
				return mapItem.title;
			} else {
				return this.applianceOfflineNotification(mapItem.objectId);
			}
		} else {
			return mapItem.title;
		}
	}

	private async saveEventMarkerLocation(): Promise<void> {
		const location = this.pendingEventMarkerPos.lat + " " + this.pendingEventMarkerPos.lng;
		await this.updateCurrentEventLocation(location);
		this.updateFirstEventRecordLocation(location);
		this.eventMarker.state = this.getEventMarkerProcessingState();
		this.originalEventLocation =  this.pendingEventMarkerPos;
		this.pendingEventMarkerPos = null;
	}

	private resetEventMarkerLocation() {
		this.eventMarker.state = this.getEventMarkerProcessingState();
		this.eventMarker.moveTo(this.originalEventLocation);
		this.pendingEventMarkerPos = null;
	}

	private get canMoveEventLocation(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "UpdateEventLocation"]) && (this.permissions.canUpdateEventLocation || this.permissions.isSystemAdmin);
	}

	private onMapLayerItemTypeAdvancedFilterChange() {
		let currentFilter = this.advancedFilters;

		(Object.keys(currentFilter)).forEach((key: string) => {
			if (typeof currentFilter[key] !== "object") {
				return;
			}

			let filter = currentFilter[key] as AdvancedFilterOption
			if (!filter.filterCondition && filter.mapLayerItemTypeId) {
				this.hiddenMapLayerItemTypes.add(filter.mapLayerItemTypeId)
			} else if (filter.mapLayerItemTypeId) {
				this.hiddenMapLayerItemTypes.delete(filter.mapLayerItemTypeId)
			}
		})
	}

	private async onMapLayerItemTypeBasicFilterChange(itemType: MapLayerItemType) {
		if (itemType) {
			if (!itemType.visible) {
				this.hiddenMapLayerItemTypes.add(itemType.mapLayerItemTypeId)
			} else {
				this.hiddenMapLayerItemTypes.delete(itemType.mapLayerItemTypeId)
			}
		}
	}

	// ----------------------------------------------------- Site Monitor Logic  ----------------------------------------------------------------------------

	// ----------------------------------------------------- Field Ops Logic  -------------------------------------------------------------------------------
	@Prop()
	private selectedAssetItemId: number | null;

	@FieldOpsStore.Action private updateAssetLocation: (asset: AssetMapLayerItem) => void;
	@FieldOpsStore.State private situationalAwarenessEventQueue: SituationalAwarenessEventQueue[];
	@FieldOpsStore.State private selectedSituationalAwarenessEvent: SituationalAwarenessEventQueue;

	@MapLayers.Getter private triggerDefaultView: boolean;
	@MapLayers.State private defaultViewForUser: google.maps.LatLngBounds;
	@MapLayers.State private computeDefaultView: DefaultViewTriggers;
	@MapLayers.Mutation private setAreaComputeDefaultView: (compute: boolean) => void;
	@MapLayers.Mutation private setAssetComputeDefaultView: (compute: boolean) => void;
	@MapLayers.Mutation private extendDefaultViewBounds: (latLng: google.maps.LatLngLiteral) => void;

	@Getter getPermissions: UserPermissions;

	private alertMarkers: IMarker[] = [];

	private currentSelectedAssetItemId: number | null;

	private get isSituationalAwarenessExternalAlertEnabled() : boolean {
		let isFeatureEnabled: boolean = this.getFeature(["SituationalAwareness","ExternalAlerts"]);
		let userHasPermission: boolean = (this.getPermissions.canViewSituationalAwarenessEvents || this.getPermissions.canDismissSituationalAwarenessEvent || this.getPermissions.canPromoteSituationalAwarenessEvent) && this.getPermissions.isSystemAdmin;

		return isFeatureEnabled && userHasPermission;
	}

	@Watch("triggerDefaultView")
	private setDefaultBounds(): void {
		if(this.isFieldOpsMode && this.defaultViewForUser != null && this.triggerDefaultView){
			this.map!.map.fitBounds(this.defaultViewForUser);
		}
	}

	@Watch("assetAndUsersItems")
	private async displayAssetItems() {
		// Early exit if component is being destroyed
		if (this.isDestroyed) {
			return;
		}
		
		if (this.isEventSearchMode) {
			return;
		}

		const assetMarkersToRemove = new Map(this.assetItemMarkers);
		const trackedMarkersToRemove = new Map(this.trackedAssetItemMarkers);

		const userType = this.mapItemTypes.find(x => x.mapLayerItemTypeId === MapLayerItemTypeIds.User)
		const assetType = this.mapItemTypes.find(x => x.title === 'Asset') ;
		const trackedAssetType = this.mapItemTypes.find(x => x.mapLayerItemTypeId === MapLayerItemTypeIds.TrackedAsset);
		const showUsers = (userType && this.useAdvancedFilters) ? this.filterCondition(this.advancedFilters.user) :
							userType ? userType.visible : true;
		const showAssets = (assetType && this.useAdvancedFilters) ? this.filterCondition(this.advancedFilters.asset) :
							assetType ? assetType.visible : true;

		let showTrackedAssets: boolean;
		if (this.isTrackedAssetsEnabled){
			showTrackedAssets = (trackedAssetType && this.useAdvancedFilters) ? this.filterCondition(this.advancedFilters.trackedAsset) :
								trackedAssetType ? trackedAssetType.visible : true;
		} else {
			showTrackedAssets = false;
		}

		if(!this.assetAndUsersItems)
		{
			return;
		}

		if(!this.map)
		{
			if (!this.isDestroyed) {
				// Clear any existing timeout before creating a new one
				if (this.displayAssetItemsTimeout != null) {
					clearTimeout(this.displayAssetItemsTimeout);
				}
				
				this.displayAssetItemsTimeout = setTimeout(() => {
					this.displayAssetItemsTimeout = null;
					if (!this.isDestroyed) {
						this.displayAssetItems();
					}
				}, 50);
			}
			return;
		}

		this.assetAndUsersItems.forEach(item => {
			// If the marker does not have a location, do not try to draw it
			if (!item.latLng) {
				return;
			}

			if(item.isTrackedAsset)
			{
				if (this.trackedAssetItemMarkers.has(item.trackedAssetId))
				{
					const marker = this.trackedAssetItemMarkers.get(item.trackedAssetId);
					marker.visible = this.filterConditionWithTrackedAsset(showTrackedAssets);
					marker.moveTo(item.latLng);
					trackedMarkersToRemove.delete(item.trackedAssetId);
					marker.marker.setTitle(this.getMarkerTitleForAsset(item));
				} else{
					const marker = this.createMarkerForAsset(item, this.filterConditionWithTrackedAsset(showTrackedAssets));
					this.trackedAssetItemMarkers.set(item.trackedAssetId, marker);
				}
			} else {
				const canShowMarker :boolean = (item.isAsset ? showAssets : showUsers) && !item.hideOnMap;
				if (this.assetItemMarkers.has(item.assetId)) {
					const marker = this.assetItemMarkers.get(item.assetId);
					marker.visible = this.filterConditionWithAssetType(canShowMarker, item);
					marker.moveTo(item.latLng);
					assetMarkersToRemove.delete(item.assetId);
					const icon = this.getIconForAssetType(item, this.selectedAssetItemId === item.assetId);
					marker.marker.setTitle(this.getMarkerTitleForAsset(item));
					marker.marker.setIcon(icon);
				} else {
					const marker = this.createMarkerForAsset(item, this.filterConditionWithAssetType(canShowMarker, item));
					if (marker) {
						this.assetItemMarkers.set(item.assetId, marker);
					}
				}
			}

			// Item latLng is already formatted correctly for extending bounds
			if (this.isFieldOpsMode && this.computeDefaultView.assets == false) {
				this.extendDefaultViewBounds(item.latLng!);
			}
		});

		assetMarkersToRemove.forEach(m => {
			try
			{
				m.remove();
				this.assetItemMarkers.delete(m.assetId);
			}
			catch(e)
			{
				console.error("Failed to delete asset marker", e)
			}
		});

		trackedMarkersToRemove.forEach(m => {
			try
			{
				m.remove();
				this.trackedAssetItemMarkers.delete(m.trackedAssetId);
			}
			catch(e)
			{
				console.error("Failed to delete tracked asset marker", e)
			}
		});

		this.setSelectedItem();

		if(this.isMobileEventShareEnabled){
			// Set up the alarm pulsing
			if(this.alarmStyle) {
				document.body.removeChild(this.alarmStyle);
				this.alarmStyle = null;
			}

			const activeEventShareAssets = this.assetAndUsersItems.filter(x => x.activeEventShare).map(x => `[title="${this.getMarkerTitleForAsset(x)}"]`);
			if(activeEventShareAssets && activeEventShareAssets.length > 0){
				if(!this.alarmStyle)
				{
					this.alarmStyle = document.createElement('style')
				}

				this.alarmStyle.innerHTML = `${activeEventShareAssets.toString()} {
						border-radius: 50%;
						box-shadow: 0 0 0 0 rgba(226, 19, 19, 1);
						transform: scale(1);
						animation: pulseOrange 2s infinite;
					}
					`
				document.body.appendChild(this.alarmStyle);
			}
		}

		// Mark the assets complete so the view can be calculated
		this.setAssetComputeDefaultView(true);
	}

	@Watch("situationalAwarenessEventQueue", { deep: true })
	private displayAlertsWithLocation(): void {
		if(this.situationalAwarenessEventQueue == null || this.situationalAwarenessEventQueue.length < 1){
			return;
		}

		let alertMarkersToRemove = (this.alertMarkers && this.alertMarkers.length < 1) ? []: [...this.alertMarkers];

		try {
			this.situationalAwarenessEventQueue.forEach(event => {
				if (!event.location) {
					return;
				}


				//check if alertMarker already exists
				const alertMarkerExists = this.alertMarkers.find(m => m.situationalAwarenessEventQueueId == event.situationalAwarenessEventQueueId);

					if (alertMarkerExists) {
					const marker = alertMarkerExists;
					marker.marker.setTitle(event.description);
					marker.moveTo(convertStringToLatLng(event.location!));
					alertMarkersToRemove = alertMarkersToRemove.filter(m => m.situationalAwarenessEventQueueId != event.situationalAwarenessEventQueueId);
					} else {
						let alertMarkerItem = this.enhancedMapping.createMarker(
							Md5.hashStr(JSON.stringify(event)),
							this.map!,
							null,
							null,
							MapLayerItemTypeIds.Alarm,
							null,
							event.description,
						    convertStringToLatLng(event.location!),
							null,
							null,
							null,
							true
						);
						alertMarkerItem.situationalAwarenessEventQueueId = event.situationalAwarenessEventQueueId;
						this.alertMarkers.push(alertMarkerItem);
				}
			});
		} catch (e) {
			console.error("Failed to create an alert marker", e);
		}

		let alertsToRemove = alertMarkersToRemove.map(m => m.situationalAwarenessEventQueueId);
		this.alertMarkers = this.alertMarkers.filter(a => !alertsToRemove.includes(a.situationalAwarenessEventQueueId));

		this.clearAlertMarkers(alertMarkersToRemove);
	}

	@Watch("selectedSituationalAwarenessEvent")
	private goToAlertLocation(): void {
		if(this.selectedSituationalAwarenessEvent == null){
			return;
		}

		const alertMarker = this.alertMarkers.find(am => am.situationalAwarenessEventQueueId == this.selectedSituationalAwarenessEvent.situationalAwarenessEventQueueId);

		if(alertMarker){
			this.centerOnMarker(alertMarker);
		}
	}

	private clearAlertMarkers(markers: IMarker[]): void {
		markers.forEach(m => {
			try
			{
				m.remove();
			}
			catch(e)
			{
				console.error("Failed to delete alert marker", e);
			}
		});
	}

	public centerOnAssetItem(assetId: number) {
		const marker = this.assetItemMarkers.get(assetId);
		if (marker) {
			this.centerOnMarker(marker);
		}
	}

	public centerOnEventQueueItem(eventRecordId: number) {
		const marker = this.eventQueueMarkers[eventRecordId];
		if (marker) {
			this.centerOnMarker(marker);
		}
	}

	private centerOnMarker(marker: any) {
		if (this.map) {
			// center map on marker's position and set it to the default 'selected' zoom level
			this.map.map.panTo(marker.marker.position);
			if (this.map.map.getZoom() < 17) {
				this.map.map.setZoom(17);
			}
		}
	}

	@Watch("selectedAssetItemId")
	private async setSelectedItem() {
		if (this.selectedAssetItemId === this.currentSelectedAssetItemId) {
			return;
		}

		if (this.selectedAssetItemId) {
			this.setMarkerIconForItem(this.selectedAssetItemId, true);
			this.centerOnAssetItem(this.selectedAssetItemId);

		}

		// clear previously selected
		if (this.currentSelectedAssetItemId && this.assetItemMarkers.has(this.currentSelectedAssetItemId)) {
			this.setMarkerIconForItem(this.currentSelectedAssetItemId, false);
		}

		this.currentSelectedAssetItemId = this.selectedAssetItemId;
	}

	private getMarkerTitleForAsset(asset: AssetMapLayerItem): string {
		let title =  asset.activeEventShare && !asset.isTrackedAsset ? `${asset.title} | Attending : ${asset.eventDetails}` : asset.title;

		if (asset.userId || asset.isTrackedAsset) {
			const lastUpdatedInMinutes = fromNow(asset.lastLocationUpdate);
			title = `${title} | Last updated : ${lastUpdatedInMinutes ? lastUpdatedInMinutes : "Unknown"}`;
		}

		if (asset.status) {
			title = `${title} | Status : ${asset.status}`;
		}

		return title;
	}

	private createMarkerForAsset(assetMapLayerItem: AssetMapLayerItem, visible: boolean) {
		if(!assetMapLayerItem || !visible) {
			return;
		}

		let markerState = new MarkerType({
			imageUrl: this.getIconForAssetType(assetMapLayerItem, false).url,
			line: null,
			fill: null,
			hidden: false,
			opacity: 1
		}, null);

		let displayItem: DisplayItem = this.createMarker({
			key: Md5.hashStr(JSON.stringify(assetMapLayerItem)),
			typeId: markerState,
			title: this.getMarkerTitleForAsset(assetMapLayerItem),
			position: assetMapLayerItem.latLng,
			mapLayerItemId: assetMapLayerItem.mapLayerItemId,
			regionPath: "",
			minElevation: null,
			maxElevation: null,
			objectId: assetMapLayerItem.assetTypeId,
			assetMarkerImageUrl: this.getIconForAssetType(assetMapLayerItem, false),
			state: {
				clickable: !assetMapLayerItem.isTrackedAsset,
				draggable: (assetMapLayerItem.isAsset && !assetMapLayerItem.isTrackedAsset),
				noCluster: true,
				iconType: "active"
			},
		});

		let marker = this.mapMarkerManager.createMarker(displayItem);

		// If this item is an asset (not a mobile user) - allow operators to move them on the map
		if(displayItem.state.draggable) {
			marker.dragend = (latLong: ILatLng) => {
				var assetClone = {...assetMapLayerItem,}
				assetClone.latLng = latLong;
				assetClone.latLong = latLong.lat + " " + latLong.lng;
				marker.moveTo(latLong);
				this.updateAssetLocation(assetClone);
			};
		}

		if (displayItem.state.clickable) {
			marker.click = async () => {
					if(assetMapLayerItem.isTrackedAsset)
					{
						return;
					}

					if (!this.isFieldOpsMode && this.isFieldOpsLicenced)
					{
						this.setViewingAsset(null);
						await this.$nextTick();
						this.setViewingAsset(assetMapLayerItem.assetId);
						var activeFieldOpsPage = this.activeSubscribers.find(x => x.subscriber === "FieldOps")
						if (this.isSiteMonitorOrEventSearchMode) {
							this.overlayAsset = {...this.assetAndUsersItems.find(x => x.assetId === assetMapLayerItem.assetId)};
							this.openFieldOpsOverlay = true;
						} else {
							if(!activeFieldOpsPage || activeFieldOpsPage.expiry <= Date.now())
								this.openFieldOps();
						}
					}

				this.mapMarkerManager.updateMarker(marker);

				this.$emit("markerClicked", assetMapLayerItem);
				this.centerOnPosition({...marker.position})
			};
		}

		this.itemsAdded[assetMapLayerItem.mapLayerItemId] = marker;

		return marker;
	}

	private openFieldOps(){
		this.openFieldOpsOverlay = false;
		this.overlayAsset = null;
		const routeData = router.resolve({
			path: "/field-ops"
		});
		window.open(
			routeData.href,
			"field-ops"
		).focus();
	}
	// ----------------------------------------------------- Field Ops Logic  -------------------------------------------------------------------------------

	// ----------------------------------------------------- Event Queue Logic  -----------------------------------------------------------------------------
	@Eventqueue.Getter("getLastUpdatedEvents") eventsLastUpdated!: number;
	@Eventqueue.Getter("getHoverEvent") hoverEvent: any;
	@Eventqueue.Getter DisplayedEvent: FilteredEvent;
	@Eventqueue.Getter getAlarmQueueFilterIDs: AlarmQueueFilterID[];
	@Eventqueue.Getter("getActiveFilterID") currentFilterID!: AlarmQueueFilterID;

	// Getter and action for boolean flag which determines whether the map is shown.
	@Eventqueue.Action setHideMapFlag : any;
	@Eventqueue.Getter("getHideMapFlag") hideMapFlag: boolean;

	@ManualRaiseStore.State selectedMapLayer: any;
	@ManualRaiseStore.State manualRaiseShown: boolean;
	@ManualRaiseStore.State("areaMapBounds") manualRaiseAreaMapBounds: any;
	@ManualRaiseStore.Mutation setAlarmMarkerPosition: any;
	@ManualRaiseStore.Mutation setSelectedMapElevation: any;
	@ManualRaiseStore.Mutation setManualRaiseShown: (visible: boolean) => void;
	@ManualRaiseStore.Mutation("setSelectedGroup") setManualRaiseSelectedGroup: (areaNode: AreaNode) => void;

	private eventQueueMarkers: ItemMap = new ItemMap();
	private manualRaiseAlarmMarker: IMarker | null = null;
	private groups: any[] = [];
	private groupSearchLoading = false;
	private selectedGroup: any = null;
	private eventIcons: MarkerArrayMap = new MarkerArrayMap();
	private createdSearchAutocomplete: boolean = false;
	private searchType: string = "area";
	private filterBoundsChanged: boolean = false;
	private filterBounds: ILatLngBounds | null = null;
	private searchAutoComplete: google.maps.places.Autocomplete | null = null;

	private setSearchAutocomplete() {
		// Clear any existing timeout before creating a new one
		if (this.searchAutocompleteTimeout != null) {
			clearTimeout(this.searchAutocompleteTimeout);
		}
		
		this.searchAutocompleteTimeout = setTimeout(() => {
			this.searchAutocompleteTimeout = null;
			if (this.isDestroyed) {
				return;
			}
			this.searchAutoComplete = new google.maps.places.Autocomplete(
				document.getElementById("tbLocation") as HTMLInputElement
			);

			this.searchAutoComplete!.bindTo("bounds", this.map!.map);
			(this.searchAutoComplete! as any).setFields(["address_components", "geometry", "name"]);

			this.searchAutoComplete!.addListener("place_changed", () => {
				let place = this.searchAutoComplete!.getPlace();

				if (place.geometry) {
					if (place.geometry.viewport) {
						this.map!.map.fitBounds(place.geometry.viewport);
					} else if (place.geometry.location) {
						this.map!.map.setCenter(place.geometry.location);
						this.map!.map.setZoom(this.defaultMinimumZoomLevel);
					}

					if (place.geometry.location && this.addingAlarm) {
						this.setManualRaiseAlarmMarkerPosition(place.geometry.location);
					}
				}
			});
		}, 0.5 * this.$config.ANIMATION_DURATION);
	}

	@Watch("selectedGroup", {deep:true})
	private selectedGroupWatch(){
		this.searchAreaSelected(this.selectedGroup)
	}

	@Watch("searchType")
	private onSearchTypeChanged(value: string) {
		if (value === "location") {
			if (!this.createdSearchAutocomplete) {
				this.createdSearchAutocomplete = true;
				this.setSearchAutocomplete();
			}
		}
	}

	private get addingAlarm() {
		return this.isEventQueueMode ? this.manualRaiseShown : false;
	}

	@Watch("currentFilterID")
	private onCurrentFilterChange(filter: any) {
		if (this.map != null) {
			this.setFilterBounds(filter.filterId, filter.regionId);
		}
	}

	@Watch("eventsLastUpdated")
	public onEventsChanged() {
		this.updateEventMarkers();
	}

	@Watch("hoverEvent")
	private onhoverEventChanged(event: any, oldEvent: any) {
		if (oldEvent && this.eventIcons[oldEvent.eventID]) {
			this.eventIcons[oldEvent.eventID].forEach(marker => {
				marker.state = marker.state.replace("highlight", "");
			});
		}

		if (event && this.eventIcons[event.eventID]) {
			this.eventIcons[event.eventID].forEach(marker => {
				marker.revertState = marker.state;
				if (marker.state == "active") {
					marker.state = "highlightactive";
				} else if (marker.state == "processing") {
					marker.state = "highlightprocessing";
				}
			});
		}
	}

	private setFilterBounds(filterID: number, regionID: number) {
		if (filterID === -1) {
			this.filterBounds = null;
			this.filterBoundsChanged = true;
		} else if (filterID === 0) {
			let openFilters = this.getAlarmQueueFilterIDs;
			if (openFilters.length > 0) {
				let requestPromises = [];

				openFilters.forEach(filter => {
					requestPromises.push(
						axiosInstance.get(
							"/AlarmQueueFilters/Bounds/" +
								filter.filterId +
								(filter.regionId ? "?regionId=" + filter.regionId : "")
						)
					);
				});

				Promise.all(requestPromises).then(values => {
					let allBounds: ILatLngBounds = null;

					values.forEach(response => {
						if (response.data && response.data.anyBounds) {
							let filterBounds = response.data as ILatLngBounds;

							if (!allBounds) {
								allBounds = {
									north: filterBounds.north,
									east: filterBounds.east,
									south: filterBounds.south,
									west: filterBounds.west
								};
							} else {
								allBounds = union(allBounds, filterBounds);
							}
						}
					});

					this.filterBounds = allBounds;
					if (allBounds) {
						this.setBounds(this.filterBounds);
					}
					this.filterBoundsChanged = true;
				});
			}
		} else {
			axiosInstance
				.get("/AlarmQueueFilters/Bounds/" + filterID + (regionID ? "?regionId=" + regionID : ""))
				.then(response => {
					let filterBounds = response.data;

					if (filterBounds && filterBounds.anyBounds) {
						this.filterBounds = {
							north: filterBounds.north,
							east: filterBounds.east,
							south: filterBounds.south,
							west: filterBounds.west
						};
						this.setBounds(this.filterBounds);
					} else {
						this.filterBounds = null;
					}

					this.filterBoundsChanged = true;
				});
		}
	}

	@Watch("DisplayedEvent")
	private onDisplayedEventChanged(event) {
		if(!this.isEventQueueMode)
			return;

		if (event && event.location) {
			const latLngString = event.location;
			this.centerOnPosition(convertStringToLatLng(latLngString), 16);
		}
	}

	@Watch("manualRaiseAreaMapBounds")
	private onManualRaiseAreaMapBoundsChanged(value: any, oldValue: any) {
		if (this.map) {
			if (value) {
				this.setBounds(value);
			} else if (oldValue) {
				this.setFilterBounds(this.currentFilterID.filterId, this.currentFilterID.regionId);
			}
		}
	}

	@Watch("selectedMapLayer")
	private async onManualRaiseSelectedLayerChanged(value: any, oldValue: any) {
		if (get(value, ["minElevation"]) && get(value, ["minElevation"]) !== get(oldValue, ["minElevation"])) {
			await this.gotoElevation({ elevationValue: value.minElevation });
		}
	}

	@Watch("addingAlarm")
	private async onAddAlarmChanged(value: boolean) {
		if(!this.isEventQueueMode) //Only allow add alarms on the event queue mode (for now)
			return;

		if (this.map) {
			if (value) {
				this.map!.setCursor("pointer");
				if (this.map!.currentViewState == "narrow") {
					await this.loadInBounds(this.map!.mapBounds, true);
				}

				for (let eventRecordID in this.eventQueueMarkers) {
					this.eventQueueMarkers[eventRecordID].state = "muted" + this.eventQueueMarkers[eventRecordID].state;
				}
			} else {
				this.map!.setCursor("grab");
				if (this.manualRaiseAlarmMarker != null) {
					// Clear callbacks before removing
					if (this.manualRaiseAlarmMarker.dragend) {
						this.manualRaiseAlarmMarker.dragend = null;
					}
					this.manualRaiseAlarmMarker.remove();
					this.manualRaiseAlarmMarker = null;
				}

				this.selectedGroup = null;
				this.setAlarmMarkerPosition("");

				for (let eventRecordID in this.eventQueueMarkers) {
					this.eventQueueMarkers[eventRecordID].state = this.eventQueueMarkers[eventRecordID].state.replace("muted", "");
				}
			}
		}
	}

	private setManualRaiseAlarmMarkerPosition(latLng: any) {
		if (this.manualRaiseAlarmMarker) {
			// Clear callbacks before removing
			if (this.manualRaiseAlarmMarker.dragend) {
				this.manualRaiseAlarmMarker.dragend = null;
			}
			this.manualRaiseAlarmMarker.remove();
			this.manualRaiseAlarmMarker = null;
		}

		this.manualRaiseAlarmMarker = this.enhancedMapping.createMarker(
			this.defaultMapMarkerKey,
			this.map!,
			null,
			null,
			MapLayerItemTypeIds.Alarm,
			null,
			"Alarm",
			latLng,
			null,
			null,
			null,
			true
		);
		this.manualRaiseAlarmMarker.state = "active";
		this.manualRaiseAlarmMarker.isDraggable = true;
		this.manualRaiseAlarmMarker.isClickable = false;

		this.manualRaiseAlarmMarker.dragend = (latLong: ILatLng) => {
			this.setAlarmMarkerPosition(latLong.lat + " " + latLong.lng);
		};

		if (latLng instanceof google.maps.LatLng) {
			this.setAlarmMarkerPosition(latLng.lat() + " " + latLng.lng());
		} else {
			this.setAlarmMarkerPosition(latLng.lat + " " + latLng.lng);
		}

		this.map!.center = latLng;
	}

	private async getAreaMarkers(): Promise<void> {
		if(this.map){
			let bounds = this.map!.mapBounds

			var mapDataResponse = await axiosInstance.post("MapLayers/AreaData", bounds);
			const mapData = mapDataResponse.data;

			if (!mapData){
				return;
			}

			this.displayAreaMarkers(mapData.areas);
		} else {
			console.log("Map is null")
		}
	}

	private async updateEventMarkers() {
		// Early exit if component is being destroyed
		if (this.isDestroyed) {
			return;
		}
		
		if ((this.manualRaiseShown && this.isEventQueueMode) || this.isEventSearchMode) {
			return;
		}

		if (this.map) {
			let events;
			//If in eventQueue only show the events in the event queue
			if(this.isEventQueueMode) {
				events = this.events;
			} else  {
				// Otherwise only show the events which we can plot (i.e. with locations)
				// create payload to post to url defined above
				const requestData = {
					filters: [],
					sortBy: 1,
					sortDesc: true,
					includeAll: true,
					onlyWithLocation: false,
					eventTypes: [EventTypes.Alarm]
				};

				// post as JSON, using the in-built transformRequest (outputs a JSON string)
				var filteredEventsResponse = await axiosInstance.post("/eventqueue", requestData, {
					headers: {
						"Content-Type": "application/json"
					},
					transformRequest: axios.defaults.transformRequest
				});
				const { eventQueue: eventsList } = filteredEventsResponse.data;

				events = eventsList
			}

			if (events == null)
				return;

			var bounds = {} as ILatLngBounds;
			var boundsSet = false;

			let eventIDs = new Set<number>();

			let eventsWithLocations = [];
			if(this.isSiteMonitorOrEventSearchMode && this.EventDetails)
			{
				events = events.filter(e => e.eventID != this.EventDetails.eventID)
				if(this.eventQueueMarkers[this.EventDetails.eventID]){
					this.eventQueueMarkers[this.EventDetails.eventID].visible = false;
					this.eventQueueMarkers[this.EventDetails.eventID].remove();
				}
			}

			var eventsWithNewLocation: number[] = [];

			// Only try and plot the events that have locations (might need to change up the event queue retrieve as it only retrieves the last 200 events)
			events.filter(e => e.location).forEach((event: any) => {
				eventsWithLocations.push(event);

				if (this.eventQueueMarkers[event.eventID] != null && this.isEventQueueMode) {
					let newLatLng: {lat:number, lng: number} = convertStringToLatLng(event.location);
					let existingLatLng = this.eventQueueMarkers[event.eventID]?.position;
					if((newLatLng && existingLatLng) && (newLatLng.lat != existingLatLng.lat || newLatLng.lng != existingLatLng.lng))
					{
						eventsWithNewLocation.push(event.eventID)
					}
				}
			});

			eventsWithLocations.forEach((event: FilteredEvent) => {

				let latLong = event.location;
				let position = convertStringToLatLng(latLong);

				// if it is a valid location add/update markers
				if(position) {
					eventIDs.add(event.eventID);

					if (this.eventQueueMarkers[event.eventID] == null) {
						let createdDisplayItem = this.createMarker({
							key: Md5.hashStr(JSON.stringify(event)),
							title: event.eventTitle,
							mapLayerItemId: event.eventID,
							eventRecordId: null,
							typeId: MapLayerItemTypeIds.Alarm,
							objectId: event.responseID,
							position: position,
							minElevation: null,
							maxElevation: null,
							visible: true,
							state: {
								clickable: true,
								draggable: false
							}
						} as DisplayItem);

						let newMarker = this.mapMarkerManager.createMarker(createdDisplayItem);
						newMarker.visible = true;
						newMarker.isClickable = true;
						newMarker.click = () => {
							if(!this.isSiteMonitorOrEventSearchMode && this.isResponseLicenced){
								this.showOverlayAlarm(event);
							}
							this.centerOnPosition({...newMarker.position});
						};
						bounds = extend(bounds, position);
						boundsSet = true;

						this.eventQueueMarkers[event.eventID] = newMarker;

						if (this.eventIcons[event.eventID] == null)
							this.eventIcons[event.eventID] = new Array<IMarker>();

						this.eventIcons[event.eventID].push(newMarker);

					} else if (this.filterBoundsChanged || eventsWithNewLocation.includes(event.eventID)) {
						bounds = extend(bounds, position);
						boundsSet = true;
					}

					if (this.isSiteMonitorMode){
						this.eventQueueMarkers[event.eventID].state =
							event.inProcessing || event.unParkAt !== null ? "backgroundprocessing" : "backgroundactive";
					} else if (this.addingAlarm) {
						this.eventQueueMarkers[event.eventID].state =
							event.inProcessing || event.unParkAt !== null ? "mutedprocessing" : "mutedactive";
					} else {
						// Highlight
						let marker = this.eventQueueMarkers[event.eventID];

						if (marker.state && marker.state.indexOf("highlight") > -1) {
							marker.state =
								event.inProcessing || event.unParkAt != null
									? "highlightprocessing"
									: "highlightactive";
						} else {
							marker.state =
								event.inProcessing || event.unParkAt != null ? "processing" : "active";
						}
					}
				}
			});

			// Scenario:
			// given an event had an eventQueueMarker
			// but the current location is not a valid location,
			// the loop below will remove the existing marker added for the
			// last valid location.
			for (var eventID in this.eventIcons) {
				if (!eventIDs.has(parseInt(eventID))) {
					this.eventIcons[eventID].forEach(eventMarker => {
						if (eventMarker.mapLayerItemId)
							delete this.eventQueueMarkers[eventMarker.mapLayerItemId!];

						eventMarker.remove();
					});

					delete this.eventIcons[eventID];
				}
			}

			if (boundsSet && this.isEventQueueMode) {
				if (this.filterBounds) {
					bounds = union(bounds, this.filterBounds!);
				}

				if (!this.manualRaiseAreaMapBounds) {
					this.setBounds(bounds);
				}
			}

			this.filterBoundsChanged = false;

			if (this.isFieldOpsMode && events.length != 0) {
				if (this.previousEvents) {
					// get difference and display notifications
					const newEvents = differenceBy(events as any[], this.previousEvents, "eventID");
					newEvents.forEach(e => {
						const msg = `${e.groupTitle} [${e.eventTitle}]`;
						let notificatinOptions: NotificationOptions = {
							group: "fieldOpsNotification",
							type: "warn",
							title: "New event:",
							text: msg,
							duration: 20000
						}

						this.$notify(notificatinOptions);
					});
				}
				this.previousEvents = events;
			}
		} else {
			console.log("Map is null");
		}
	}

	private async searchAreaSelected(area: AreaNode | null) {
		//If we have a region for the area, use the region first
		let areaRegion = this.regions ? this.regions.get(area.id) : null;
		if (areaRegion) {
			const regionBounds = getRegionBounds(areaRegion.poly.getPath());
			this.map!.map.fitBounds(regionBounds);
			return;
		}

		let areaLayerBounds = await api.getBoundsForArea(area.id);
		if (areaLayerBounds && (areaLayerBounds.east !== -180 && areaLayerBounds.west !== 180)) {
			this.setBounds(areaLayerBounds);
		} else if (area.latLong) {
			this.centerOnPosition(convertStringToLatLng(area.latLong));
		} else {
			let geoCoderResponse = await geoCoder.decodeLocation(area.address, this.mapKey);
			if (geoCoderResponse) {
				this.centerOnPosition(geoCoderResponse.location, 17);
			}
		}
	}

	private setBounds(bounds: ILatLngBounds) {
		if (!bounds)
			return;

		bounds.north = bounds.north || 400;
		bounds.east = bounds.east || 400;
		bounds.west = bounds.west || 400;
		bounds.south = bounds.south || 400;

		this.map!.mapBounds = bounds;
	}
	// ----------------------------------------------------- Event Queue Logic  -----------------------------------------------------------------------------
	@Watch("openFieldOpsOverlay")
	private async setCanShareEventWithAsset(): Promise<void> {
		if (this.openFieldOpsOverlay
			&& this.overlayAsset
			&& this.overlayAsset.shareable
			&& (!this.overlayAsset.isAsset || (this.overlayAsset.isAsset && !this.overlayAsset.activeEventShare))
			&& this.isMobileEventShareEnabled)
		{
			if (!this.overlayAsset.userId)
			{
				this.canShareEventWithAsset = true;
			}
			
			try {
				let canShareEventWithAssetUser = await api.canShareEventWithUser(this.EventDetails.eventID, this.overlayAsset.userId);
				this.canShareEventWithAsset = canShareEventWithAssetUser;
			}
			catch (e) {
				this.$notify({
					type: "error",
					title: "Share Event",
					text: "You do not have permission to share this event."
				})
			}
		}
		else {
			this.canShareEventWithAsset = false;
		}
	}

	private async shareAssetToEvent(){
		if(this.EventDetails && this.overlayAsset){
			await api.shareEventWithAsset(this.EventDetails.eventID, this.overlayAsset.assetId);
		}
		this.overlayAsset = null;
		this.openFieldOpsOverlay = false;
	}

	// ----------------------------------------------------- Regions  -----------------------------------------------------------------------------
	private regions: Map<number, {poly: google.maps.Polygon, label: MapLabel, groupId: number, areaDetails: string }> = new Map<number, {poly: google.maps.Polygon, label: MapLabel, groupId: number, areaDetails: string }>();
	private showRegions: boolean = true;

	private get isAreaRegionsEnabled(): boolean {
		return this.getFeature(["Areas", "Regions"]);
	}

	private async loadRegions(): Promise<void> {
		let regions = await api.getAllRegions();
		//Generate the polygons for every region that has a path
		regions.filter(x => x.path).forEach(region => {
			const polygonOptions = getPolygonOptionsForRegion(region);
			const regionPoly = new google.maps.Polygon({
				map: this.map!.map,
				paths: JSON.parse(region.path),
				...polygonOptions,
				// Make polygon non-clickable so clicks pass through to the map
				// This allows us to use a single map click listener instead of one per region
				clickable: false
			});
			const position = getRegionBounds(regionPoly.getPath()).getCenter();

			const label = new this.MapLabel(
				{
					text: ellipseAfterX(region.areaDetails),
					position: position,
					fontSize: 12,
					fontColor: "black",
					labelInBackground: true,
					strokeColor: region.borderColor,
					map: this.map!.map
				});
			label.set('position', position);

			this.regions.set(region.groupId, { 
				poly: regionPoly, 
				label: label, 
				groupId: region.groupId, 
				areaDetails: region.areaDetails 
			});
		});
	}

	/**
	 * Check if a click occurred within any region polygon
	 */
	private checkRegionClick(latLng: google.maps.LatLng): void {
		if(!this.regions || this.regions.size === 0) {
			return;
		}

		// Only process region clicks if we're in a mode that needs them
		if (!this.addingAssetLocation && !this.addingAlarm) {
			return;
		}

		// Check each visible region to see if click is within it
		this.regions.forEach((regionData) => {

			if (!regionData.poly.getMap()) {
				return;
			}

			if (google.maps.geometry.poly.containsLocation(latLng, regionData.poly)) {
				if(this.addingAssetLocation) {
					this.$emit("setClickLocation", latLng.lat() + " " + latLng.lng());
				}

				if(this.addingAlarm) {
					this.setManualRaiseAlarmMarkerPosition(latLng);
					this.setManualRaiseSelectedGroup({
						id: regionData.groupId,
						label: regionData.areaDetails
					});
				}
			}
		});
	}

	private toggleRegions(): void {
		this.showRegions = this.useAdvancedFilters ? this.filterCondition(this.advancedFilters.regions) : !this.showRegions;
		if(this.showRegions && this.regions){
			this.regions.forEach(m => {
				m.poly.setMap(this.map!.map);
				if(m.label)
					m.label.setMap(this.map!.map);
			})
		} else if (this.regions) {
			this.regions.forEach(m => {
				m.poly.setMap(null);
				if(m.label)
					m.label.setMap(null);
			})
		}
	}
	private async setExternalMapLayersList(): Promise<void> {
		if (this.externalLayersList && this.externalLayersList.length > 0) {
			await this.onExternalMapLayersListChanged(this.externalLayersList);
		}
	}

	@Watch("externalLayersList")
	public async onExternalMapLayersListChanged(list: ExternalMapLayer[]) {
		// send list of layers to mapping library, all logic and functions is handled in there
		if (this.enhancedMapping) {

			if (!this.map || !this.map.map)
			{
				// Need to wait until the map is ready
				if (!this.isDestroyed) {
					// Clear any existing timeout before creating a new one
					if (this.externalMapLayersTimeout != null) {
						clearTimeout(this.externalMapLayersTimeout);
					}
					
					this.externalMapLayersTimeout = setTimeout(function() {
						this.externalMapLayersTimeout = null;
						if (!this.isDestroyed) {
							this.onExternalMapLayersListChanged(list); 
						}
					}.bind(this), 1000);
				}
				return;
			}
			await this.enhancedMapping.loadExternalMapLayers(list, this.map.map);
		} else {
			if (!this.isDestroyed) {
				// Clear any existing timeout before creating a new one
				if (this.externalMapLayersTimeout != null) {
					clearTimeout(this.externalMapLayersTimeout);
				}
				
				this.externalMapLayersTimeout = setTimeout(function() {
					this.externalMapLayersTimeout = null;
					if (!this.isDestroyed) {
						this.onExternalMapLayersListChanged(list); 
					}
				}.bind(this), 1000);
			}
		}
	}

	public async toggleExternalMapLayerVisibility(mapLayerItem: ExternalMapLayer) {
		let updatedMapLayer = cloneDeep(mapLayerItem);
		updatedMapLayer.displaying = !mapLayerItem.displaying;

		await this.updateExternalMapLayerVisibility(updatedMapLayer);
	}
	// ----------------------------------------------------- Regions  -----------------------------------------------------------------------------
}
